import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  Skeleton,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import SelectAllOutlinedIcon from "@mui/icons-material/SelectAllOutlined";
import DeselectOutlinedIcon from "@mui/icons-material/DeselectOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  cachedMeetingPhotoFile,
  fetchMeetingPhotoAsFile,
  invalidateMeetingPhotoCache,
} from "./meetingPhotoDrag";
import { dashGlassCardSx } from "../MyDashboard/myDashboardStyles";
import {
  meetingActionBtnSx,
  meetingCaptureActionTileSx,
  meetingCaptureCardSx,
  meetingCaptureRailBtnSx,
  meetingCaptureRailSx,
  meetingChipRowSx,
  meetingDesktopAsideRailSx,
  meetingDesktopAsideSx,
  meetingDesktopMainSx,
  meetingDesktopSplitSx,
  meetingSessionBodySx,
  meetingHeaderBtnSx,
  meetingStudioContainedBtnSx,
  meetingHeaderIconBtnSx,
  meetingHeaderBadgeSx,
  meetingHeaderToolGroupLabelSx,
  meetingHeaderToolGroupSx,
  meetingHeaderToolIconBtnSx,
  meetingHeaderToolbarSx,
  meetingPageShellSx,
  meetingQuietFieldSx,
  meetingSectionGap,
  meetingSessionHeaderSx,
  meetingStackedPanelSx,
  MEETING_SURFACE_BG,
} from "./meetingUiStyles";
import {
  claimMeetingRecording,
  createMeetingFreeNote,
  deleteMeetingFreeNote,
  deleteMeetingSession,
  deleteMeetingPhoto,
  getMeetingSession,
  getMeetingSessionTranscripts,
  getMeetingPhotoDetail,
  mergeMeetingPhotoAiDetails,
  meetingMediaUrl,
  moveMeetingPhotos,
  patchMeetingFreeNote,
  patchMeetingPhoto,
  rotateMeetingPhoto,
  patchMeetingSession,
  releaseMeetingRecording,
  stopMeetingSession,
  regenMeetingMedia,
  unlinkMeetingPhotosMuestras,
  unleaveMeetingSamples,
  uploadMeetingAudioChunk,
} from "../../api/Meetings/meetingSessionsApi";
import { meetingPhotoIsLinked } from "./meetingLinkedSampleLabel";
import { socket } from "../../socket";
import MeetingFreeNotesPanel from "./MeetingFreeNotesPanel";
import {
  countUploadingMedia,
  enqueueMeetingPhotoUpload,
  listSessionMediaUploads,
  mergePhotosWithMediaUploads,
  subscribeMeetingMediaUploads,
  waitForSessionMediaUploads,
} from "./meetingMediaUploadQueue";
import {
  formatElapsed,
  canDeleteMeetingFromToken,
  formatMeetingMediaMaxMb,
  formatMeetingPhotoMaxMb,
  formatMeetingVideoMaxMb,
  meetingMediaMaxBytesFor,
  formatMeetingMediaWarnMb,
  hasMeetingConsent,
  isMeetingConsentComplete,
  isMeetingMediaOversize,
  shouldWarnMeetingMediaSize,
  mediaDaysLeftFromSession,
  MEETING_MEDIA_RETENTION_DAYS,
  notesByPhotoId,
  audioCouplingByPhotoId,
  photoOffsetFromStart,
  toDateInputValue,
  pickMeetingSummaryLocale,
  buildMeetingSampleNotes,
  sanitizeMeetingImportPhotoIds,
  canCreateSampleFromSelection,
  keepNewerRotatedPhoto,
} from "../../utils/meetingRecorder";
import { useMeetingRecorder } from "./useMeetingRecorder";
import MeetingBrandSelect from "./MeetingBrandSelect";
import MeetingAttendeeSelect from "./MeetingAttendeeSelect";
import MeetingClientContactSelect from "./MeetingClientContactSelect";
import MeetingDeleteDialog from "./MeetingDeleteDialog";
import MeetingConsentPanel from "./MeetingConsentPanel";
import MeetingSummaryPrint, {
  printMeetingSummary,
} from "./MeetingSummaryPrint";
import MeetingRegenMediaDialog from "./MeetingRegenMediaDialog";
import MeetingMediaGroupsFeed from "./MeetingMediaGroupsFeed";
import MeetingCreateSampleDialog from "./MeetingCreateSampleDialog";
import MeetingCreateMoodboardDialog from "./MeetingCreateMoodboardDialog";
import MeetingLinkMuestraDialog from "./MeetingLinkMuestraDialog";
import MeetingLeaveSamplesDialog from "./MeetingLeaveSamplesDialog";
import MeetingUnlinkMuestraDialog from "./MeetingUnlinkMuestraDialog";
import MeetingUnleaveSamplesDialog from "./MeetingUnleaveSamplesDialog";
import MeetingTeamNoteCaptureDialog from "./MeetingTeamNoteCaptureDialog";
import MeetingMoveMediaDialog from "./MeetingMoveMediaDialog";
import MeetingMediaPreviewDialog from "./MeetingMediaPreviewDialog";
import { downloadMeetingLeaveSamplesPdf } from "./meetingLeaveSamplesPdf";
import DrawerMuestraV2 from "../Muestras/Drawer/DrawerMuestraV2";
import {
  getMuestrasTipo,
  getMuestrasEstado,
  getTallas,
  getColoresMuestras,
  getProveedores,
  getMarcas as getMuestraMarcas,
  getEscandalloGrupos,
  getMuestraById,
  importMeetingMediaToMuestra,
} from "../../api/Muestras/muestraApi";
import { jwtDecode } from "jwt-decode";
import { getUserById } from "../../api/User/userApi";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

const CAPTURE_RAIL_STORAGE_KEY = "crm.meeting.captureRailOpen";
const WORKBENCH_MEDIA_THRESHOLD = 40;

function CollapsedCaptureRail({
  theme,
  t,
  timerLabel,
  recording,
  busy,
  canRecord,
  canTakePhoto,
  notesCount,
  isNarrow,
  parked,
  onExpand,
  onOpenNotes,
  onOpenAi,
  onOpenDetails,
  onStop,
  onStart,
  onPhoto,
  onVideo,
  onGallery,
  onMediaFile,
  photoInputRef,
  videoInputRef,
  galleryInputRef,
}) {
  return (
    <Paper
      sx={{
        ...meetingCaptureRailSx(theme),
        // Tooltips/spans envueltos: no encoger ni solapar en la fila móvil.
        "& > span": {
          flex: "0 0 auto",
          display: "inline-flex",
        },
        ...(parked
          ? {
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              p: 0.5,
              gap: 0.5,
              position: "relative",
              top: "auto",
              flexDirection: "column",
              flexWrap: "nowrap",
            }
          : {}),
      }}
      elevation={0}
    >
      <Tooltip title={t("Meetings.captureRailExpand")}>
        <IconButton
          onClick={onExpand}
          sx={meetingCaptureRailBtnSx}
          aria-label={t("Meetings.captureRailExpand")}
        >
          <ChevronRightOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          fontFamily: 'Manrope, "Helvetica Neue", Arial, sans-serif',
          fontVariantNumeric: "tabular-nums",
          textAlign: "center",
          display: parked ? "block" : { xs: "none", md: "block" },
          lineHeight: 1.1,
        }}
      >
        {timerLabel}
      </Typography>
      {recording ? (
        <Tooltip title={t("Meetings.stop")}>
          <IconButton
            onClick={onStop}
            disabled={busy}
            sx={{
              ...meetingCaptureRailBtnSx,
              color: "#c62828",
              borderColor: "rgba(198,40,40,0.45)",
              bgcolor: "rgba(198,40,40,0.1)",
            }}
            aria-label={t("Meetings.stop")}
          >
            <StopCircleOutlinedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={t("Meetings.record")}>
          <IconButton
            onClick={onStart}
            disabled={!canRecord || busy}
            sx={meetingCaptureRailBtnSx}
            aria-label={t("Meetings.record")}
          >
            <MicNoneOutlinedIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t("Meetings.photo")}>
        <span>
          <IconButton
            onClick={onPhoto}
            disabled={!canTakePhoto}
            sx={meetingCaptureRailBtnSx}
            aria-label={t("Meetings.photo")}
          >
            <PhotoCameraOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t("Meetings.video")}>
        <span>
          <IconButton
            onClick={onVideo}
            disabled={!canTakePhoto}
            sx={meetingCaptureRailBtnSx}
            aria-label={t("Meetings.video")}
          >
            <VideocamOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t("Meetings.fromGallery")}>
        <span>
          <IconButton
            onClick={onGallery}
            disabled={!canTakePhoto}
            sx={meetingCaptureRailBtnSx}
            aria-label={t("Meetings.fromGallery")}
          >
            <CollectionsOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t("Meetings.captureRailNotes")}>
        <IconButton
          onClick={() => {
            if (typeof onOpenNotes === "function") onOpenNotes();
            else onExpand?.();
          }}
          sx={{
            ...meetingCaptureRailBtnSx,
            bgcolor: "rgba(127,157,172,0.18)",
          }}
          aria-label={t("Meetings.captureRailNotes")}
        >
          <Badge
            color="secondary"
            badgeContent={notesCount}
            max={99}
            sx={meetingHeaderBadgeSx}
          >
            <NotesOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title={t("Meetings.aiActionsTitle")}>
        <IconButton
          onClick={() => {
            if (typeof onOpenAi === "function") onOpenAi();
            else onExpand?.();
          }}
          sx={meetingCaptureRailBtnSx}
          aria-label={t("Meetings.aiActionsTitle")}
        >
          <RefreshOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("Meetings.sessionDetails")}>
        <IconButton
          onClick={() => {
            if (typeof onOpenDetails === "function") onOpenDetails();
            else onExpand?.();
          }}
          sx={meetingCaptureRailBtnSx}
          aria-label={t("Meetings.sessionDetails")}
        >
          <TuneOutlinedIcon />
        </IconButton>
      </Tooltip>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*,image/heic,image/heif,.heic,.heif"
        capture={isNarrow ? "environment" : undefined}
        hidden
        onChange={onMediaFile}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        multiple
        capture={isNarrow ? "environment" : undefined}
        hidden
        onChange={onMediaFile}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,image/heic,image/heif,.heic,.heif,video/*,application/pdf,.pdf"
        multiple
        hidden
        onChange={onMediaFile}
      />
    </Paper>
  );
}

const EMPTY_MEDIA_GROUPS = [];

export default function MeetingSessionPage({
  embedded = false,
  sessionId = null,
  captureSlotEl = null,
  onCaptureRailChange = null,
  onRegisterUnpark = null,
  onSessionChange = null,
  /** Si se pasa (p. ej. Compás), cierra sin navegar a /reuniones. */
  onLeave = null,
} = {}) {
  const { id: paramId } = useParams();
  const id =
    sessionId != null && sessionId !== "" ? String(sessionId) : paramId;
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up("md"));
  /** Móvil + iPad: chips de cabecera más compactos. */
  const compactHeader = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const shellSx = embedded
    ? {
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        height: "100%",
        minHeight: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflow: "hidden",
        // Respirar dentro de Compás (no pegado a árbol / borde derecho)
        px: { xs: 1, sm: 1.25, md: 1.5 },
        pt: { xs: 0.75, md: 1 },
        pb: { xs: 1, md: 1.25 },
        bgcolor:
          theme.palette.mode === "dark" ? "transparent" : MEETING_SURFACE_BG,
        background:
          theme.palette.mode === "dark" ? "transparent" : MEETING_SURFACE_BG,
      }
    : {
        ...meetingPageShellSx(contentMargin, theme),
        // Mantener overflowY del shell (auto en móvil) para no recortar Datos
        overflowX: "hidden",
      };
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const audioPausedForVideoRef = useRef(false);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const errorClearTimerRef = useRef(null);

  /** Errores de red breves: texto claro + auto-cierre (no bloquear la visita). */
  const pushMeetingError = useCallback(
    (err, fallback) => {
      const apiMsg = err?.response?.data?.error;
      const raw = String(apiMsg || err?.message || "").trim();
      const transientNet =
        !err?.response &&
        (err?.code === "ERR_NETWORK" ||
          err?.code === "ECONNABORTED" ||
          /^network error$/i.test(raw));
      const message = transientNet
        ? t("Meetings.networkErrorBrief")
        : apiMsg || raw || fallback || t("Meetings.loadError");
      setError(message);
      if (errorClearTimerRef.current) {
        window.clearTimeout(errorClearTimerRef.current);
        errorClearTimerRef.current = null;
      }
      if (transientNet) {
        errorClearTimerRef.current = window.setTimeout(() => {
          setError("");
          errorClearTimerRef.current = null;
        }, 4500);
      }
    },
    [t],
  );
  const [busy, setBusy] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [captionPhotoId, setCaptionPhotoId] = useState(null);
  const captionIsVideo = useMemo(() => {
    const photo = (session?.photos || []).find(
      (p) => Number(p.id) === Number(captionPhotoId),
    );
    if (!photo) return false;
    return (
      photo.kind === "video" ||
      String(photo.mimeType || "").startsWith("video/")
    );
  }, [session?.photos, captionPhotoId]);
  const [customerDraft, setCustomerDraft] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [titleEditing, setTitleEditing] = useState(false);
  const [dateDraft, setDateDraft] = useState(() => toDateInputValue());
  const [attendeeIds, setAttendeeIds] = useState([]);
  const [clientContactIds, setClientContactIds] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [consentDraft, setConsentDraft] = useState({
    consentClientsInformed: false,
    consentRecordingAccepted: false,
    consentAiAccepted: false,
  });
  const [savingConsent, setSavingConsent] = useState(false);
  const [mediaWarning, setMediaWarning] = useState("");
  const [mediaUploadingCount, setMediaUploadingCount] = useState(0);
  const [leaveUploadOpen, setLeaveUploadOpen] = useState(false);
  const [leavingWait, setLeavingWait] = useState(false);
  const [regenMediaOpen, setRegenMediaOpen] = useState(false);
  const [regenMediaBusy, setRegenMediaBusy] = useState(false);
  const [savingMediaGroups, setSavingMediaGroups] = useState(false);
  const mediaGroupsSaveGenRef = useRef(0);
  const mediaGroupsSavingRef = useRef(false);
  const [summaryLang, setSummaryLang] = useState("es");
  const [previewPhotoId, setPreviewPhotoId] = useState(null);
  const rotateChainRef = useRef(new Map());
  const [previewDragFile, setPreviewDragFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [highlightPhotoId, setHighlightPhotoId] = useState(null);
  const [expandedNoteIds, setExpandedNoteIds] = useState(() => new Set());
  const [productGateOpen, setProductGateOpen] = useState(false);
  const [pendingSamplePhotos, setPendingSamplePhotos] = useState([]);
  const [emptyTeamNoteWarnOpen, setEmptyTeamNoteWarnOpen] = useState(false);
  const [emptyTeamNotePhotoIds, setEmptyTeamNotePhotoIds] = useState([]);
  const [needGroupWarnOpen, setNeedGroupWarnOpen] = useState(false);
  const [linkMuestraOpen, setLinkMuestraOpen] = useState(false);
  const [linkingMuestra, setLinkingMuestra] = useState(false);
  const [leaveSamplesOpen, setLeaveSamplesOpen] = useState(false);
  const [leaveSamplesListOpen, setLeaveSamplesListOpen] = useState(false);
  const [leaveSamplesPdfBusy, setLeaveSamplesPdfBusy] = useState(false);
  const [muestraDrawerOpen, setMuestraDrawerOpen] = useState(false);
  const [newMuestraDraft, setNewMuestraDraft] = useState(null);
  const [selectedProductForMuestra, setSelectedProductForMuestra] =
    useState(null);
  const [pendingMeetingMedia, setPendingMeetingMedia] = useState(null);
  const [muestraCatalogs, setMuestraCatalogs] = useState(null);
  const [muestraEditMode, setMuestraEditMode] = useState(false);
  const [mediaSelectedCount, setMediaSelectedCount] = useState(0);
  const [mediaSelectionAllLinked, setMediaSelectionAllLinked] = useState(false);
  const [unlinkingMuestra, setUnlinkingMuestra] = useState(false);
  const [unleavingSampleId, setUnleavingSampleId] = useState(null);
  const [unlinkConfirmPhotos, setUnlinkConfirmPhotos] = useState(null);
  const [unleaveConfirmItem, setUnleaveConfirmItem] = useState(null);
  const [noteSaving, setNoteSaving] = useState(false);
  const [freeNotesSheetOpen, setFreeNotesSheetOpen] = useState(false);
  const [moveMediaOpen, setMoveMediaOpen] = useState(false);
  const [movingMedia, setMovingMedia] = useState(false);
  const [moodboardOpen, setMoodboardOpen] = useState(false);
  const [moodboardPhotos, setMoodboardPhotos] = useState([]);
  const [allowMoodboard, setAllowMoodboard] = useState(false);
  const [captureRailOpen, setCaptureRailOpen] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CAPTURE_RAIL_STORAGE_KEY);
      if (raw === "0") return false;
      if (raw === "1") return true;
    } catch (_) {
      /* ignore */
    }
    return true;
  });
  const [railAutoApplied, setRailAutoApplied] = useState(false);
  const [mediaToolbarHost, setMediaToolbarHost] = useState(null);
  const [moodboardMenuEl, setMoodboardMenuEl] = useState(null);
  const mediaFeedRef = useRef(null);
  const softRefreshBusyRef = useRef(false);
  /** ids borrados hace poco: el poll/socket no debe reintroducir la miniatura fantasma */
  const recentlyDeletedPhotoIdsRef = useRef(new Map());

  const filterOutRecentlyDeletedPhotos = useCallback((photos) => {
    const tomb = recentlyDeletedPhotoIdsRef.current;
    const now = Date.now();
    for (const [pid, expiresAt] of tomb) {
      if (expiresAt <= now) tomb.delete(pid);
    }
    if (!tomb.size) return photos || [];
    return (photos || []).filter((p) => !tomb.has(Number(p.id)));
  }, []);

  const markPhotoRecentlyDeleted = useCallback((photoId) => {
    const n = Number(photoId);
    if (!Number.isFinite(n)) return;
    recentlyDeletedPhotoIdsRef.current.set(n, Date.now() + 45_000);
  }, []);
  const canDelete = useMemo(() => {
    try {
      return canDeleteMeetingFromToken(
        jwtDecode(localStorage.getItem("token") || ""),
      );
    } catch {
      return false;
    }
  }, []);
  const currentUserId = useMemo(() => {
    try {
      const decoded = jwtDecode(localStorage.getItem("token") || "");
      const n = Number(decoded?.userId ?? decoded?.id);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }, []);
  const userCanCreateSample = useMemo(() => {
    try {
      const decoded = jwtDecode(localStorage.getItem("token") || "");
      return Boolean(decoded?.PermisoCreateSample);
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!currentUserId) {
          if (!cancelled) setAllowMoodboard(false);
          return;
        }
        const user = await getUserById(currentUserId);
        if (!cancelled) {
          setAllowMoodboard(Boolean(user?.Permiso_Moodboard));
        }
      } catch {
        if (!cancelled) setAllowMoodboard(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  const applySessionPayload = useCallback(
    (data) => {
      if (!data) return null;
      const photos = filterOutRecentlyDeletedPhotos(
        mergePhotosWithMediaUploads(data.photos, id),
      );
      const next = { ...data, photos };
      setSession(next);
      setMediaUploadingCount(countUploadingMedia(id));
      setCustomerDraft(data.customerName || "");
      setTitleDraft(data.title || "");
      setDateDraft(toDateInputValue(data.meetingDate || data.startedAt));
      setAttendeeIds(
        (data.attendees || [])
          .map((a) => Number(a.userId))
          .filter((n) => Number.isFinite(n) && n > 0),
      );
      setClientContactIds(
        (data.clientContacts || [])
          .map((c) => Number(c.contactId))
          .filter((n) => Number.isFinite(n) && n > 0),
      );
      return next;
    },
    [filterOutRecentlyDeletedPhotos, id],
  );

  const load = useCallback(
    async (opts = {}) => {
      const quiet = Boolean(opts.quiet);
      const keepError = Boolean(opts.keepError);
      if (!quiet) setLoading(true);
      if (!keepError) setError("");
      try {
        const data = await getMeetingSession(id);
        return applySessionPayload(data);
      } catch (err) {
        // Poll / refrescos en segundo plano: no spamear "Network Error" rojo
        const transientNet =
          !err?.response &&
          (err?.code === "ERR_NETWORK" ||
            err?.code === "ECONNABORTED" ||
            /^network error$/i.test(String(err?.message || "")));
        if (quiet && transientNet) return null;
        const msg =
          err.response?.data?.error || err.message || t("Meetings.loadError");
        setError(
          /^network error$/i.test(String(msg))
            ? t("Meetings.networkErrorBrief")
            : msg,
        );
        return null;
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [applySessionPayload, id, t],
  );

  const softRefreshFromPeers = useCallback(async () => {
    if (softRefreshBusyRef.current) return;
    softRefreshBusyRef.current = true;
    try {
      const data = await getMeetingSession(id);
      if (!data) return;
      setSession((prev) => {
        const mergedIncoming = mergeMeetingPhotoAiDetails(
          data.photos,
          prev?.photos,
        );
        const prevById = new Map(
          (prev?.photos || []).map((p) => [Number(p.id), p]),
        );
        const photos = filterOutRecentlyDeletedPhotos(
          mergePhotosWithMediaUploads(mergedIncoming, id).map((p) =>
            keepNewerRotatedPhoto(p, prevById.get(Number(p.id))),
          ),
        );
        if (!prev) return { ...data, photos };
        // Mientras guardamos grupos, no pisar el tablero local con un poll viejo
        // (provoca el flasheo «mete / saca» al arrastrar fotos al álbum).
        const mediaGroups = mediaGroupsSavingRef.current
          ? prev.mediaGroups
          : data.mediaGroups;
        // Conservar transcripts ya hidratados (el GET grid los manda vacíos)
        const prevChunks = new Map(
          (prev.audioChunks || []).map((c) => [Number(c.id), c]),
        );
        const audioChunks = (data.audioChunks || prev.audioChunks || []).map(
          (c) => {
            const old = prevChunks.get(Number(c.id));
            const text =
              String(c.transcriptText || "").trim() ||
              String(old?.transcriptText || "").trim();
            return text
              ? { ...c, transcriptText: text, hasTranscript: true }
              : c;
          },
        );
        const summary = data.summary
          ? {
              ...data.summary,
              fullTranscript:
                String(data.summary.fullTranscript || "").trim() ||
                String(prev.summary?.fullTranscript || "").trim() ||
                "",
              finalTranscript:
                String(data.summary.finalTranscript || "").trim() ||
                String(prev.summary?.finalTranscript || "").trim() ||
                "",
              transcript:
                String(data.summary.transcript || "").trim() ||
                String(prev.summary?.transcript || "").trim() ||
                "",
            }
          : prev.summary;
        return {
          ...prev,
          ...data,
          photos,
          audioChunks,
          summary,
          freeNotes: data.freeNotes || [],
          mediaGroups,
          leftWithClient: data.leftWithClient,
          recordingByUserId: data.recordingByUserId ?? null,
          recordingByUsername: data.recordingByUsername || "",
          recordingStartedAt: data.recordingStartedAt || null,
          audioCount: data.audioCount,
          photoCount: data.photoCount,
          videoCount: data.videoCount,
          imageCount: data.imageCount,
          status: data.status,
          updatedAt: data.updatedAt,
        };
      });
      setMediaUploadingCount(countUploadingMedia(id));
    } catch {
      /* silencio: poll/socket de colaboración */
    } finally {
      softRefreshBusyRef.current = false;
    }
  }, [filterOutRecentlyDeletedPhotos, id]);

  const ensurePhotoAiDetail = useCallback(
    async (photoId) => {
      const pid = Number(photoId);
      if (!Number.isFinite(pid) || !id) return null;
      const current = (session?.photos || []).find((p) => Number(p.id) === pid);
      const hasBody =
        String(current?.visionText || "").trim() ||
        String(current?.transcriptText || "").trim();
      if (hasBody) return current;
      try {
        const detail = await getMeetingPhotoDetail(id, pid);
        setSession((prev) => {
          if (!prev?.photos) return prev;
          return {
            ...prev,
            photos: prev.photos.map((p) =>
              Number(p.id) === pid
                ? {
                    ...p,
                    visionText: detail.visionText || "",
                    transcriptText: detail.transcriptText || "",
                    hasVision: Boolean(detail.hasVision),
                    hasTranscript: Boolean(detail.hasTranscript),
                    aiStatus: detail.aiStatus || p.aiStatus,
                    aiError: detail.aiError || p.aiError,
                    aiProcessedAt: detail.aiProcessedAt || p.aiProcessedAt,
                    teamNote:
                      detail.teamNote != null ? detail.teamNote : p.teamNote,
                  }
                : p,
            ),
          };
        });
        return detail;
      } catch {
        return null;
      }
    },
    [id, session?.photos],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Tras el GET grid: transcripts en segundo plano (notas acopladas / acta).
  useEffect(() => {
    if (!id || loading || !session?.id) return undefined;
    let cancelled = false;
    getMeetingSessionTranscripts(id)
      .then((bundle) => {
        if (cancelled || !bundle) return;
        setSession((prev) => {
          if (!prev || Number(prev.id) !== Number(id)) return prev;
          const byChunkId = new Map(
            (bundle.audioChunks || []).map((c) => [Number(c.id), c]),
          );
          const audioChunks = (prev.audioChunks || []).map((c) => {
            const extra = byChunkId.get(Number(c.id));
            if (!extra?.transcriptText) return c;
            return {
              ...c,
              transcriptText: extra.transcriptText,
              hasTranscript: true,
            };
          });
          const summary =
            prev.summary && bundle.summary
              ? {
                  ...prev.summary,
                  fullTranscript:
                    bundle.summary.fullTranscript ||
                    prev.summary.fullTranscript ||
                    "",
                  finalTranscript:
                    bundle.summary.finalTranscript ||
                    prev.summary.finalTranscript ||
                    "",
                  transcript:
                    bundle.summary.transcript || prev.summary.transcript || "",
                  hasTranscript: true,
                }
              : prev.summary;
          return { ...prev, audioChunks, summary };
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, loading, session?.id]);

  useEffect(() => {
    // Poll/socket solo cuando ya hay sesión (no competir con el primer GET).
    if (!id || loading || !session) return undefined;
    const meetingId = Number(id);
    socket.emit("join-meeting", { meetingSessionId: meetingId });
    const onUpdated = (payload) => {
      if (Number(payload?.meetingSessionId) !== meetingId) return;
      softRefreshFromPeers();
    };
    socket.on("meeting-updated", onUpdated);
    const poll = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      softRefreshFromPeers();
    }, 8000);
    const onVisible = () => {
      if (document.visibilityState === "visible") softRefreshFromPeers();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      socket.emit("leave-meeting", { meetingSessionId: meetingId });
      socket.off("meeting-updated", onUpdated);
      window.clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [id, loading, session?.id, softRefreshFromPeers]);

  useEffect(() => {
    if (!id) return undefined;
    const meetingId = Number(id);
    return () => {
      releaseMeetingRecording(meetingId).catch(() => {});
    };
  }, [id]);

  useEffect(() => {
    setMediaUploadingCount(countUploadingMedia(id));
    return subscribeMeetingMediaUploads(() => {
      setMediaUploadingCount(countUploadingMedia(id));
      setSession((prev) => {
        if (!prev) return prev;
        const base = (prev.photos || []).filter((p) => !p.pendingUpload);
        return {
          ...prev,
          photos: mergePhotosWithMediaUploads(base, id),
        };
      });
    });
  }, [id]);

  const leaveToList = useCallback(() => {
    if (typeof onLeave === "function") {
      onLeave();
      return;
    }
    navigate("/reuniones");
  }, [onLeave, navigate]);

  const requestLeaveMeeting = useCallback(() => {
    if (countUploadingMedia(id) > 0) {
      setLeaveUploadOpen(true);
      return;
    }
    leaveToList();
  }, [id, leaveToList]);

  const leaveAfterUploads = useCallback(async () => {
    setLeavingWait(true);
    try {
      await waitForSessionMediaUploads(id);
    } finally {
      setLeavingWait(false);
      setLeaveUploadOpen(false);
      leaveToList();
    }
  }, [id, leaveToList]);

  const leaveAnyway = useCallback(() => {
    setLeaveUploadOpen(false);
    leaveToList();
  }, [leaveToList]);

  const onChunk = useCallback(
    async ({ file, seq, durationMs }) => {
      await uploadMeetingAudioChunk(id, file, { seq, durationMs });
      // Evitar GET completo: pisa el feed si hay una foto/vídeo subiendo a la vez.
      setSession((prev) =>
        prev
          ? {
              ...prev,
              audioCount: Math.max(
                Number(prev.audioCount) || 0,
                Number(seq) + 1,
              ),
            }
          : prev,
      );
    },
    [id],
  );

  const onError = useCallback(
    (err) => {
      setError(err?.message || t("Meetings.recordError"));
    },
    [t],
  );

  const recorder = useMeetingRecorder({ onChunk, onError });

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteMeetingSession(id);
      setDeleteOpen(false);
      leaveToList();
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.deleteError"),
      );
    } finally {
      setDeleting(false);
    }
  };

  const recordingHeldByOther =
    session &&
    Number(session.recordingByUserId) > 0 &&
    Number(session.recordingByUserId) !== Number(currentUserId) &&
    !recorder.isRecording;
  const canRecord =
    session &&
    hasMeetingConsent(session) &&
    ["recording", "failed", "ready"].includes(session.status) &&
    !recorder.isRecording &&
    !recordingHeldByOther;
  const canMarkReady =
    session &&
    hasMeetingConsent(session) &&
    (session.status === "recording" || session.status === "failed");
  const canTakePhoto =
    session &&
    hasMeetingConsent(session) &&
    ["recording", "failed", "ready", "processing"].includes(session.status);
  const canSummarizePermission = session?.canSummarize !== false;
  const canSummarizeNow =
    session &&
    canSummarizePermission &&
    hasMeetingConsent(session) &&
    !recorder.isRecording &&
    !recorder.isPausedForMedia &&
    ["recording", "failed", "ready"].includes(session.status) &&
    (session.audioCount > 0 || session.photoCount > 0);
  // «Lista» sin acta (solo marcar) no cuenta como resumen hecho.
  const summaryDone = Boolean(
    session?.summary && session.summary.status === "done",
  );
  const needsConsent = session && !hasMeetingConsent(session);

  const handleSaveConsent = async () => {
    if (!isMeetingConsentComplete(consentDraft)) {
      setError(t("Meetings.consentRequired"));
      return;
    }
    setSavingConsent(true);
    setError("");
    try {
      const data = await patchMeetingSession(id, consentDraft);
      setSession(data);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.consentError"),
      );
    } finally {
      setSavingConsent(false);
    }
  };

  const handleStart = async () => {
    if (!hasMeetingConsent(session)) {
      setError(t("Meetings.consentRequired"));
      return;
    }
    setError("");
    setBusy(true);
    try {
      const claimed = await claimMeetingRecording(id);
      if (claimed) applySessionPayload(claimed);
      await recorder.start(session?.audioCount || 0);
    } catch (err) {
      try {
        await releaseMeetingRecording(id);
      } catch {
        /* ignore */
      }
      setError(
        err.response?.data?.error || err.message || t("Meetings.micDenied"),
      );
      await softRefreshFromPeers();
    } finally {
      setBusy(false);
    }
  };

  const handleStopAudio = async () => {
    setBusy(true);
    try {
      await recorder.stop();
      try {
        await releaseMeetingRecording(id);
      } catch {
        /* ignore */
      }
      await load({ quiet: true, keepError: true });
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.stopError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleMarkReady = async () => {
    setBusy(true);
    setError("");
    try {
      if (recorder.isRecording) {
        await recorder.stop();
      }
      try {
        await releaseMeetingRecording(id);
      } catch {
        /* ignore */
      }
      await stopMeetingSession(id);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.endMeetingError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCreateFreeNote = async (text) => {
    setNoteSaving(true);
    setError("");
    try {
      const note = await createMeetingFreeNote(id, text);
      setSession((prev) => {
        if (!prev) return prev;
        const list = [...(prev.freeNotes || []), note];
        return { ...prev, freeNotes: list };
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.freeNotesError"),
      );
      throw err;
    } finally {
      setNoteSaving(false);
    }
  };

  const handleUpdateFreeNote = async (note, text) => {
    if (!note?.id) return;
    setNoteSaving(true);
    setError("");
    try {
      const updated = await patchMeetingFreeNote(id, note.id, text);
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          freeNotes: (prev.freeNotes || []).map((n) =>
            Number(n.id) === Number(note.id) ? { ...n, ...updated } : n,
          ),
        };
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.freeNotesUpdateError"),
      );
      throw err;
    } finally {
      setNoteSaving(false);
    }
  };

  const handleDeleteFreeNote = async (note) => {
    if (!note?.id) return;
    setNoteSaving(true);
    setError("");
    try {
      await deleteMeetingFreeNote(id, note.id);
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          freeNotes: (prev.freeNotes || []).filter(
            (n) => Number(n.id) !== Number(note.id),
          ),
        };
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.freeNotesDeleteError"),
      );
    } finally {
      setNoteSaving(false);
    }
  };

  const mediaPickerCleanupRef = useRef(null);
  const mediaUploadInFlightRef = useRef(false);
  /** Esperando archivo del selector de vídeo (evita reanudar el mic antes de tiempo). */
  const videoPickerPendingRef = useRef(false);

  const clearMediaPickerListeners = useCallback(() => {
    if (typeof mediaPickerCleanupRef.current === "function") {
      mediaPickerCleanupRef.current();
      mediaPickerCleanupRef.current = null;
    }
  }, []);

  const resumeAudioIfPausedForVideo = useCallback(async () => {
    if (!audioPausedForVideoRef.current && !recorder.isPausedForMedia) return;
    audioPausedForVideoRef.current = false;
    try {
      await recorder.resumeAfterMediaCapture();
    } catch (err) {
      setError(
        err.message ||
          t("Meetings.micDenied") ||
          "No se pudo reanudar el audio",
      );
    }
  }, [recorder.isPausedForMedia, recorder.resumeAfterMediaCapture, t]);

  const uploadOneMediaFile = (file) => {
    const maxBytes = meetingMediaMaxBytesFor(file);
    if (isMeetingMediaOversize(file.size, maxBytes)) {
      setError(
        t("Meetings.mediaHardLimit", {
          maxMb: formatMeetingMediaMaxMb(maxBytes),
        }),
      );
      setMediaWarning("");
      throw new Error("oversize");
    }
    if (shouldWarnMeetingMediaSize(file.size, { maxBytes })) {
      setMediaWarning(
        t("Meetings.mediaHeavyWarn", {
          warnMb: formatMeetingMediaWarnMb(),
          sizeMb: Math.round(file.size / (1024 * 1024)),
        }),
      );
    }
    const offsetMs = photoOffsetFromStart(session.startedAt);
    const isVideoFile =
      String(file.type || "").startsWith("video/") ||
      /\.(mov|mp4|m4v|webm|avi)$/i.test(file.name || "");
    const isPdfFile =
      String(file.type || "").toLowerCase() === "application/pdf" ||
      /\.pdf$/i.test(file.name || "");
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const localUrl = URL.createObjectURL(file);
    const pendingPhoto = {
      id: localId,
      fileUrl: localUrl,
      mimeType:
        file.type ||
        (isVideoFile
          ? "video/mp4"
          : isPdfFile
            ? "application/pdf"
            : "image/jpeg"),
      kind: isVideoFile ? "video" : isPdfFile ? "pdf" : "photo",
      caption: "",
      teamNote: "",
      linkedMuestras: [],
      offsetMs,
      offsetLabel: "",
      pendingUpload: true,
    };
    const entry = enqueueMeetingPhotoUpload({
      sessionId: id,
      file,
      pendingPhoto,
      offsetMs,
      capturedAt: new Date().toISOString(),
    });
    setSession((prev) => {
      if (!prev) return prev;
      const base = (prev.photos || []).filter((p) => !p.pendingUpload);
      return {
        ...prev,
        photos: mergePhotosWithMediaUploads(base, id),
        photoCount: (prev.photoCount || 0) + 1,
        imageCount: isVideoFile
          ? (prev.imageCount ?? prev.photoCount ?? 0)
          : (prev.imageCount ?? prev.photoCount ?? 0) + 1,
        videoCount: isVideoFile
          ? (prev.videoCount || 0) + 1
          : prev.videoCount || 0,
      };
    });
    entry.promise.catch((err) => {
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          photoCount: Math.max(0, (prev.photoCount || 1) - 1),
          imageCount: isVideoFile
            ? prev.imageCount
            : Math.max(0, (prev.imageCount || 1) - 1),
          videoCount: isVideoFile
            ? Math.max(0, (prev.videoCount || 1) - 1)
            : prev.videoCount || 0,
          photos: mergePhotosWithMediaUploads(
            (prev.photos || []).filter((p) => !p.pendingUpload),
            id,
          ),
        };
      });
      return err;
    });
    return entry;
  };

  const uploadMediaFiles = async (rawFiles, { fromPicker = false } = {}) => {
    const files = (Array.isArray(rawFiles) ? rawFiles : []).filter(Boolean);
    const hasVideo = files.some(
      (f) =>
        String(f?.type || "").startsWith("video/") ||
        /\.(mov|mp4|m4v|webm|avi)$/i.test(f?.name || ""),
    );
    // Con vídeo: no reabrir el mic hasta que la subida termine (iOS se atasca si no).
    const resumeAfterUpload = Boolean(fromPicker && hasVideo);
    const resumeNow = async () => {
      if (fromPicker && !resumeAfterUpload) {
        await resumeAudioIfPausedForVideo();
      }
    };
    const resumeWhenDone = async () => {
      if (resumeAfterUpload) {
        await resumeAudioIfPausedForVideo();
      }
    };

    if (!files.length || !session) {
      if (fromPicker) await resumeAudioIfPausedForVideo();
      return;
    }
    if (!hasMeetingConsent(session)) {
      setError(t("Meetings.consentRequired"));
      if (fromPicker) await resumeAudioIfPausedForVideo();
      return;
    }
    mediaUploadInFlightRef.current = true;
    setError("");
    setMediaWarning("");

    // Una sola pieza: dialog de nota al instante (no espera a la subida).
    if (files.length === 1) {
      try {
        const entry = uploadOneMediaFile(files[0]);
        setCaptionPhotoId(entry.localId || entry.pendingPhoto?.id);
        setCaption("");
        setCaptionOpen(true);
        mediaUploadInFlightRef.current = false;
        await resumeNow();
        entry.promise
          .then((photo) => {
            if (photo?.id) setCaptionPhotoId(photo.id);
            load({ quiet: true, keepError: true });
          })
          .catch((err) => {
            if (err?.message === "oversize") return;
            setError(
              err?.response?.data?.error ||
                err?.message ||
                t("Meetings.photoError"),
            );
            setCaptionOpen(false);
            setCaptionPhotoId(null);
            setCaption("");
          })
          .finally(() => {
            resumeWhenDone();
          });
        return;
      } catch (err) {
        mediaUploadInFlightRef.current = false;
        if (fromPicker) await resumeAudioIfPausedForVideo();
        if (err?.message !== "oversize") {
          setError(
            err?.response?.data?.error ||
              err?.message ||
              t("Meetings.photoError"),
          );
        }
        return;
      }
    }

    const uploadPromises = files.map((file) => {
      try {
        const entry = uploadOneMediaFile(file);
        return entry.promise.catch((err) => {
          if (err?.message === "oversize") return { __oversize: true };
          setError(
            err.response?.data?.error ||
              err.message ||
              t("Meetings.photoError"),
          );
          return null;
        });
      } catch (err) {
        if (err?.message === "oversize")
          return Promise.resolve({ __oversize: true });
        setError(
          err.response?.data?.error || err.message || t("Meetings.photoError"),
        );
        return Promise.resolve(null);
      }
    });
    mediaUploadInFlightRef.current = false;
    await resumeNow();

    const results = await Promise.all(uploadPromises);
    await resumeWhenDone();
    const uploaded = results.filter((r) => r && !r.__oversize);
    if (uploaded.length) {
      load({ quiet: true, keepError: true });
    }
  };

  const handleMediaFile = async (event) => {
    clearMediaPickerListeners();
    videoPickerPendingRef.current = false;
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    await uploadMediaFiles(files, { fromPicker: true });
  };

  const handleDropMediaFiles = (files) => {
    uploadMediaFiles(files, { fromPicker: false });
  };

  const openPhotoPicker = () => {
    if (!canTakePhoto) return;
    photoInputRef.current?.click();
  };

  const openGalleryPicker = () => {
    if (!canTakePhoto) return;
    galleryInputRef.current?.click();
  };

  const openVideoPicker = async () => {
    if (!canTakePhoto) return;
    clearMediaPickerListeners();
    let settled = false;
    let cancelResumeTimer = null;
    videoPickerPendingRef.current = true;
    const safetyTimer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      videoPickerPendingRef.current = false;
      clearMediaPickerListeners();
      resumeAudioIfPausedForVideo();
    }, 120000);
    try {
      if (recorder.isRecording && !recorder.isPausedForMedia) {
        setMediaWarning(t("Meetings.videoPausesAudio"));
        audioPausedForVideoRef.current = true;
        await recorder.pauseForMediaCapture();
      }
      // Si cancela el selector, al volver a la app reanudamos el audio.
      // Esperar bastante: en iOS el `change` del vídeo llega DESPUÉS del focus;
      // si reanudamos el mic antes, la subida del vídeo se queda colgada.
      let sawBlur = false;
      const onBlur = () => {
        sawBlur = true;
      };
      const tryResumeAfterCancel = () => {
        if (settled) return;
        if (mediaUploadInFlightRef.current) return;
        if (videoPickerPendingRef.current === false) return;
        // Aún puede llegar el archivo; solo cancelar si sigue pending y sin subida
        settled = true;
        videoPickerPendingRef.current = false;
        window.clearTimeout(safetyTimer);
        resumeAudioIfPausedForVideo();
      };
      const onReturn = () => {
        if (!sawBlur) return;
        window.removeEventListener("focus", onReturn);
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("blur", onBlur);
        if (cancelResumeTimer) window.clearTimeout(cancelResumeTimer);
        // Ventana amplia: iOS puede tardar en entregar el fichero tras cerrar la cámara.
        cancelResumeTimer = window.setTimeout(tryResumeAfterCancel, 8000);
      };
      const onVisible = () => {
        if (document.visibilityState === "visible") onReturn();
      };
      window.addEventListener("blur", onBlur);
      window.addEventListener("focus", onReturn);
      document.addEventListener("visibilitychange", onVisible);
      mediaPickerCleanupRef.current = () => {
        settled = true;
        window.clearTimeout(safetyTimer);
        if (cancelResumeTimer) window.clearTimeout(cancelResumeTimer);
        window.removeEventListener("focus", onReturn);
        window.removeEventListener("blur", onBlur);
        document.removeEventListener("visibilitychange", onVisible);
      };
      videoInputRef.current?.click();
    } catch (err) {
      window.clearTimeout(safetyTimer);
      if (cancelResumeTimer) window.clearTimeout(cancelResumeTimer);
      clearMediaPickerListeners();
      videoPickerPendingRef.current = false;
      audioPausedForVideoRef.current = false;
      setError(err.message || t("Meetings.photoError"));
      try {
        await recorder.resumeAfterMediaCapture();
      } catch {
        /* ya parado */
      }
    }
  };

  const saveCaption = async () => {
    if (!captionPhotoId) {
      setCaptionOpen(false);
      return;
    }
    const text = String(caption || "").trim();
    let photoId = captionPhotoId;
    try {
      // Si aún es el id local, espera a que termine la subida.
      if (String(photoId).startsWith("local-")) {
        const entry = listSessionMediaUploads(id).find(
          (e) => String(e.localId) === String(photoId),
        );
        if (entry?.promise) {
          const photo = await entry.promise;
          photoId = photo?.id;
        }
      }
      if (text && photoId && !String(photoId).startsWith("local-")) {
        const updated = await patchMeetingPhoto(id, photoId, {
          teamNote: text,
        });
        setSession((prev) => {
          if (!prev?.photos) return prev;
          return {
            ...prev,
            photos: prev.photos.map((p) =>
              Number(p.id) === Number(photoId)
                ? { ...p, teamNote: updated.teamNote || text }
                : p,
            ),
          };
        });
      }
    } catch (err) {
      pushMeetingError(err);
    } finally {
      setCaptionOpen(false);
      setCaptionPhotoId(null);
      setCaption("");
    }
  };

  const handleRegenMedia = async (photoIds) => {
    if (regenMediaBusy || busy) return;
    setRegenMediaBusy(true);
    setError("");
    try {
      const data = await regenMeetingMedia(id, photoIds);
      const regen = Array.isArray(data?.regen) ? data.regen : [];
      const fail = regen.filter((row) => !row.ok).length;
      const next = { ...data };
      delete next.regen;
      setSession(next);
      if (fail) {
        setError(
          t("Meetings.regenMediaPartial", {
            ok: regen.length - fail,
            fail,
          }),
        );
      }
      setRegenMediaOpen(false);
    } catch (err) {
      const apiError = err.response?.data?.error || err.message;
      setError(String(apiError || t("Meetings.summarizeError")));
    } finally {
      setRegenMediaBusy(false);
    }
  };

  const commitTitle = useCallback(
    async (raw) => {
      const next = String(raw ?? titleDraft ?? "").trim();
      const prev = String(session?.title || "").trim();
      setTitleEditing(false);
      if (!next) {
        setTitleDraft(session?.title || "");
        return;
      }
      if (next === prev) {
        setTitleDraft(prev);
        return;
      }
      setTitleDraft(next);
      try {
        const data = await patchMeetingSession(id, { title: next });
        setSession(data);
        const savedTitle = data?.title || next;
        setTitleDraft(savedTitle);
        onSessionChange?.({
          id: Number(id),
          title: savedTitle,
        });
      } catch (err) {
        setTitleDraft(session?.title || "");
        pushMeetingError(err);
      }
    },
    [id, onSessionChange, session?.title, titleDraft],
  );

  const summary = session?.summary;
  const displaySummary = useMemo(() => {
    if (!summary) return null;
    const pack = pickMeetingSummaryLocale(summary, summaryLang);
    return { ...summary, ...pack };
  }, [summary, summaryLang]);
  const photos = session?.photos || [];
  const audioChunks = session?.audioChunks || [];
  const workbenchMediaCount = useMemo(() => {
    const images = Number(session?.imageCount ?? session?.photoCount ?? 0) || 0;
    const videos = Number(session?.videoCount ?? 0) || 0;
    const fromList = Array.isArray(photos)
      ? photos.filter((p) => p && !p.pendingUpload).length
      : 0;
    return Math.max(images + videos, fromList);
  }, [session?.imageCount, session?.photoCount, session?.videoCount, photos]);
  const workbenchMode = workbenchMediaCount >= WORKBENCH_MEDIA_THRESHOLD;
  /** Siempre Galería al abrir; Detalle queda a mano en el toggle. */
  const mediaLayoutMode = "gallery";

  useEffect(() => {
    if (!session || railAutoApplied) return;
    if (!workbenchMode) {
      setRailAutoApplied(true);
      return;
    }
    try {
      if (sessionStorage.getItem(CAPTURE_RAIL_STORAGE_KEY) == null) {
        setCaptureRailOpen(false);
      }
    } catch (_) {
      setCaptureRailOpen(false);
    }
    setRailAutoApplied(true);
  }, [session, workbenchMode, railAutoApplied]);

  const pendingRailFocusRef = useRef(null);
  const [railFocusTick, setRailFocusTick] = useState(0);

  const setCaptureRailOpenPersist = useCallback((open) => {
    setCaptureRailOpen(open);
    try {
      sessionStorage.setItem(CAPTURE_RAIL_STORAGE_KEY, open ? "1" : "0");
    } catch (_) {
      /* ignore */
    }
  }, []);

  const openCollapsedRailSection = useCallback(
    (section) => {
      pendingRailFocusRef.current = section;
      setCaptureRailOpenPersist(true);
      setRailFocusTick((n) => n + 1);
    },
    [setCaptureRailOpenPersist],
  );

  useEffect(() => {
    if (!captureRailOpen || !pendingRailFocusRef.current) return;
    const section = pendingRailFocusRef.current;
    pendingRailFocusRef.current = null;
    const sel =
      section === "ai"
        ? "[data-meeting-ai]"
        : section === "details"
          ? "[data-meeting-details]"
          : null;
    if (!sel) return;
    const focus = () => {
      document.querySelector(sel)?.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    };
    // El panel se monta al expandir: doble rAF + timeout corto
    requestAnimationFrame(() => {
      requestAnimationFrame(focus);
    });
    const t = window.setTimeout(focus, 120);
    return () => window.clearTimeout(t);
  }, [captureRailOpen, railFocusTick]);

  useEffect(() => {
    if (
      !embedded ||
      !isDesktopLayout ||
      typeof onCaptureRailChange !== "function"
    ) {
      return;
    }
    onCaptureRailChange(captureRailOpen);
  }, [embedded, isDesktopLayout, captureRailOpen, onCaptureRailChange]);

  useEffect(() => {
    if (typeof onRegisterUnpark !== "function") return undefined;
    onRegisterUnpark(() => setCaptureRailOpenPersist(true));
    return () => onRegisterUnpark(null);
  }, [onRegisterUnpark, setCaptureRailOpenPersist]);

  const notesById = useMemo(
    () =>
      notesByPhotoId(
        displaySummary,
        summary?.transcript ||
          summary?.finalTranscript ||
          summary?.fullTranscript,
        {
          photos,
          // EN: el audio de fotos es Whisper en ES; usamos visualNotes traducidas.
          includePhotoVisualNotes: summaryLang === "en",
        },
      ),
    [displaySummary, summary, photos, summaryLang],
  );

  const handleSaveMediaGroups = useCallback(
    async (groups) => {
      const gen = ++mediaGroupsSaveGenRef.current;
      mediaGroupsSavingRef.current = true;
      setSavingMediaGroups(true);
      setError("");
      // Optimistic: la UI local manda; evita «necesitas grupo» antes de que responda el BACK
      setSession((prev) =>
        prev
          ? { ...prev, mediaGroups: Array.isArray(groups) ? groups : [] }
          : prev,
      );
      try {
        const data = await patchMeetingSession(id, { mediaGroups: groups });
        if (gen !== mediaGroupsSaveGenRef.current) return;
        const serverGroups = Array.isArray(data?.mediaGroups)
          ? data.mediaGroups
          : [];
        setSession((prev) => {
          if (!prev && !data) return prev;
          const base = Array.isArray(data?.photos)
            ? data
            : { ...(prev || {}), ...(data || {}), photos: prev?.photos };
          const nextGroups =
            Array.isArray(groups) && groups.length && !serverGroups.length
              ? groups
              : serverGroups.length
                ? serverGroups
                : prev?.mediaGroups || [];
          return {
            ...base,
            photos: mergePhotosWithMediaUploads(base.photos || [], id),
            mediaGroups: nextGroups,
          };
        });
      } catch (err) {
        if (gen !== mediaGroupsSaveGenRef.current) return;
        pushMeetingError(err);
      } finally {
        if (gen === mediaGroupsSaveGenRef.current) {
          mediaGroupsSavingRef.current = false;
          setSavingMediaGroups(false);
        }
      }
    },
    [id],
  );

  const handleSaveTeamNote = useCallback(
    async (photoId, teamNote) => {
      setError("");
      try {
        const updated = await patchMeetingPhoto(id, photoId, { teamNote });
        setSession((prev) => {
          if (!prev?.photos) return prev;
          return {
            ...prev,
            photos: prev.photos.map((p) =>
              Number(p.id) === Number(photoId)
                ? { ...p, teamNote: updated.teamNote || "" }
                : p,
            ),
          };
        });
      } catch (err) {
        pushMeetingError(err);
        throw err;
      }
    },
    [id],
  );

  const handleRotatePhoto = useCallback(
    (photoId, degrees) => {
      const key = Number(photoId);
      const turn = ((Number(degrees) % 360) + 360) % 360;
      if (!turn) return Promise.resolve();
      setError("");
      const applyUpdated = (updated) => {
        invalidateMeetingPhotoCache(photoId);
        setSession((prev) => {
          if (!prev?.photos) return prev;
          return {
            ...prev,
            photos: prev.photos.map((p) => {
              if (Number(p.id) !== key) return p;
              const nextUrl = updated.fileUrl || p.fileUrl;
              const displayWasFile =
                !p.displayFileUrl || p.displayFileUrl === p.fileUrl;
              return {
                ...p,
                fileUrl: nextUrl,
                thumbUrl: updated.thumbUrl || "",
                mimeType: updated.mimeType || p.mimeType,
                displayFileUrl: displayWasFile ? nextUrl : p.displayFileUrl,
              };
            }),
          };
        });
      };
      const prevJob = rotateChainRef.current.get(key) || Promise.resolve();
      const job = prevJob
        .catch(() => {})
        .then(async () => {
          try {
            const updated = await rotateMeetingPhoto(id, photoId, degrees);
            const urls = [updated?.fileUrl, updated?.thumbUrl]
              .map((raw) => meetingMediaUrl(raw || ""))
              .filter(Boolean);
            if (urls.length) {
              await Promise.all(
                urls.map(
                  (src) =>
                    new Promise((resolve) => {
                      const img = new Image();
                      const done = () => resolve();
                      img.onload = done;
                      img.onerror = done;
                      img.src = src;
                    }),
                ),
              );
            }
            applyUpdated(updated);
          } catch (err) {
            pushMeetingError(err);
            throw err;
          }
        });
      rotateChainRef.current.set(key, job);
      return job;
    },
    [id],
  );

  const handleDeleteMedia = useCallback(
    async (photoId) => {
      setError("");
      markPhotoRecentlyDeleted(photoId);
      // Optimistic: quitar solo esa pieza (el DELETE a veces devolvía sesión sin photos)
      setSession((prev) => {
        if (!prev?.photos) return prev;
        const photos = prev.photos.filter(
          (p) => Number(p.id) !== Number(photoId),
        );
        const removed = prev.photos.find(
          (p) => Number(p.id) === Number(photoId),
        );
        const wasVideo =
          removed &&
          (removed.kind === "video" ||
            String(removed.mimeType || "").startsWith("video/"));
        return {
          ...prev,
          photos,
          photoCount: Math.max(0, (prev.photoCount || 1) - 1),
          imageCount: wasVideo
            ? prev.imageCount
            : Math.max(0, (prev.imageCount || 1) - 1),
          videoCount: wasVideo
            ? Math.max(0, (prev.videoCount || 1) - 1)
            : prev.videoCount || 0,
          mediaGroups: (prev.mediaGroups || [])
            .map((g) => ({
              ...g,
              mediaIds: (g.mediaIds || []).filter(
                (mid) => Number(mid) !== Number(photoId),
              ),
            }))
            .filter(
              (g) =>
                (g.mediaIds || []).length > 0 || String(g.label || "").trim(),
            ),
        };
      });
      setPreviewPhotoId((prev) =>
        Number(prev) === Number(photoId) ? null : prev,
      );
      setCaptionPhotoId((prev) =>
        Number(prev) === Number(photoId) ? null : prev,
      );
      try {
        const data = await deleteMeetingPhoto(id, photoId);
        setSession((prev) => {
          if (Array.isArray(data?.photos)) {
            return {
              ...data,
              photos: filterOutRecentlyDeletedPhotos(
                mergePhotosWithMediaUploads(data.photos, id),
              ),
              mediaGroups: Array.isArray(data.mediaGroups)
                ? data.mediaGroups
                : prev?.mediaGroups || [],
            };
          }
          // No pisar el feed si el API no trae photos
          if (!prev) return data;
          return {
            ...prev,
            ...data,
            photos: filterOutRecentlyDeletedPhotos(prev.photos),
            mediaGroups: Array.isArray(data?.mediaGroups)
              ? data.mediaGroups
              : prev.mediaGroups,
          };
        });
      } catch (err) {
        recentlyDeletedPhotoIdsRef.current.delete(Number(photoId));
        await load({ quiet: true, keepError: true });
        setError(
          err.response?.data?.error ||
            err.message ||
            t("Meetings.deleteMediaError"),
        );
        throw err;
      }
    },
    [filterOutRecentlyDeletedPhotos, id, load, markPhotoRecentlyDeleted, t],
  );

  const handleConfirmMoveMedia = useCallback(
    async (targetMeetingSessionId) => {
      const photoIds = mediaFeedRef.current?.getSelectedPhotoIds?.() || [];
      if (!photoIds.length || movingMedia) return;
      setMovingMedia(true);
      setError("");
      try {
        const data = await moveMeetingPhotos(id, {
          targetMeetingSessionId,
          photoIds,
        });
        setSession((prev) => {
          if (Array.isArray(data?.photos)) {
            return {
              ...data,
              photos: mergePhotosWithMediaUploads(data.photos, id),
              mediaGroups: Array.isArray(data.mediaGroups)
                ? data.mediaGroups
                : prev?.mediaGroups || [],
            };
          }
          return data || prev;
        });
        mediaFeedRef.current?.clearSelection?.();
        setMediaSelectedCount(0);
        setMediaSelectionAllLinked(false);
        setMoveMediaOpen(false);
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.message ||
            t("Meetings.moveMediaError"),
        );
      } finally {
        setMovingMedia(false);
      }
    },
    [id, movingMedia, t],
  );

  const ensureMuestraCatalogs = useCallback(async () => {
    if (muestraCatalogs) return muestraCatalogs;
    const [
      tiposM,
      estados,
      tallasData,
      coloresData,
      proveedoresData,
      marcasData,
      gruposEscandallo,
    ] = await Promise.all([
      getMuestrasTipo(),
      getMuestrasEstado(),
      getTallas(),
      getColoresMuestras(),
      getProveedores(),
      getMuestraMarcas(),
      getEscandalloGrupos(),
    ]);
    const next = {
      tiposMuestra: tiposM || [],
      tiposEstado: estados || [],
      tiposTallas: tallasData || [],
      tiposColores: coloresData || [],
      tiposProveedores: proveedoresData || [],
      tiposMarcas: marcasData || [],
      escandalloGrupos: gruposEscandallo || [],
    };
    setMuestraCatalogs(next);
    return next;
  }, [muestraCatalogs]);

  const openCreateSampleFlow = useCallback((selectedPhotos) => {
    const missing = selectedPhotos
      .filter((p) => !String(p?.teamNote || "").trim())
      .map((p) => Number(p.id))
      .filter((id) => Number.isFinite(id));
    if (missing.length) {
      setEmptyTeamNotePhotoIds(missing);
      setEmptyTeamNoteWarnOpen(true);
      return;
    }
    setEmptyTeamNotePhotoIds([]);
    setProductGateOpen(true);
  }, []);

  const handleMediaSelectionChange = useCallback((info) => {
    if (typeof info === "number") {
      setMediaSelectedCount(info);
      setMediaSelectionAllLinked(false);
      return;
    }
    setMediaSelectedCount(Number(info?.count) || 0);
    setMediaSelectionAllLinked(Boolean(info?.allLinked));
  }, []);

  const handleCreateSampleFromMedia = useCallback(
    async (selectedPhotos) => {
      if (!selectedPhotos?.length) return;
      if (selectedPhotos.some(meetingPhotoIsLinked)) {
        setError(t("Meetings.createSampleAlreadyLinked"));
        return;
      }
      setPendingSamplePhotos(selectedPhotos);
      let groups = session?.mediaGroups || EMPTY_MEDIA_GROUPS;
      try {
        if (typeof mediaFeedRef.current?.flushSaveGroups === "function") {
          const flushed = await mediaFeedRef.current.flushSaveGroups();
          if (Array.isArray(flushed)) groups = flushed;
        } else if (typeof mediaFeedRef.current?.getMediaGroups === "function") {
          groups = mediaFeedRef.current.getMediaGroups() || groups;
        }
      } catch (_) {
        if (typeof mediaFeedRef.current?.getMediaGroups === "function") {
          groups = mediaFeedRef.current.getMediaGroups() || groups;
        }
      }
      const groupCheck = canCreateSampleFromSelection(
        selectedPhotos.map((p) => p.id),
        groups,
      );
      if (!groupCheck.ok) {
        setNeedGroupWarnOpen(true);
        return;
      }
      openCreateSampleFlow(selectedPhotos);
    },
    [openCreateSampleFlow, session?.mediaGroups, t],
  );

  const applyImportedMeetingLinks = useCallback((result) => {
    const copied = Array.isArray(result?.copied) ? result.copied : [];
    if (!copied.length) return;
    const byPhotoId = new Map(copied.map((c) => [Number(c.photoId), c]));
    setSession((prev) => {
      if (!prev?.photos?.length) return prev;
      let changed = false;
      const photosNext = prev.photos.map((p) => {
        const hit = byPhotoId.get(Number(p.id));
        if (!hit) return p;
        changed = true;
        const linked =
          Array.isArray(hit.linkedMuestras) && hit.linkedMuestras.length
            ? hit.linkedMuestras
            : (() => {
                const prevLinks =
                  Array.isArray(p.linkedMuestras) && p.linkedMuestras.length
                    ? p.linkedMuestras.slice()
                    : p.idMuestra
                      ? [
                          {
                            idMuestra: p.idMuestra,
                            muestraNombre: p.muestraNombre || "",
                          },
                        ]
                      : [];
                const mid = Number(hit.idMuestra);
                if (!Number.isFinite(mid) || mid <= 0) return prevLinks;
                const without = prevLinks.filter(
                  (x) => Number(x.idMuestra) !== mid,
                );
                without.push({
                  idMuestra: mid,
                  muestraNombre: hit.muestraNombre || p.muestraNombre || "",
                  ...(hit.muestraNombreCliente
                    ? { muestraNombreCliente: hit.muestraNombreCliente }
                    : {}),
                });
                return without;
              })();
        const last = linked[linked.length - 1];
        return {
          ...p,
          linkedMuestras: linked,
          idMuestra: last?.idMuestra ?? hit.idMuestra ?? p.idMuestra,
          muestraNombre:
            last?.muestraNombre || hit.muestraNombre || p.muestraNombre || "",
          muestraNombreCliente:
            last?.muestraNombreCliente ||
            hit.muestraNombreCliente ||
            p.muestraNombreCliente ||
            "",
        };
      });
      return changed ? { ...prev, photos: photosNext } : prev;
    });
  }, []);

  const handleLinkSampleFromMedia = useCallback(
    async (selectedPhotos) => {
      if (!selectedPhotos?.length) return;
      const unlinked = selectedPhotos.filter((p) => !meetingPhotoIsLinked(p));
      if (!unlinked.length) {
        setError(t("Meetings.linkMuestraAlready"));
        return;
      }
      setPendingSamplePhotos(unlinked);
      let groups = session?.mediaGroups || EMPTY_MEDIA_GROUPS;
      try {
        if (typeof mediaFeedRef.current?.flushSaveGroups === "function") {
          const flushed = await mediaFeedRef.current.flushSaveGroups();
          if (Array.isArray(flushed)) groups = flushed;
        } else if (typeof mediaFeedRef.current?.getMediaGroups === "function") {
          groups = mediaFeedRef.current.getMediaGroups() || groups;
        }
      } catch (_) {
        if (typeof mediaFeedRef.current?.getMediaGroups === "function") {
          groups = mediaFeedRef.current.getMediaGroups() || groups;
        }
      }
      const groupCheck = canCreateSampleFromSelection(
        unlinked.map((p) => p.id),
        groups,
      );
      if (!groupCheck.ok) {
        setNeedGroupWarnOpen(true);
        return;
      }
      setLinkMuestraOpen(true);
    },
    [session?.mediaGroups, t],
  );

  const handleUnlinkSampleFromMedia = useCallback((selectedPhotos) => {
    const photos = (selectedPhotos || []).filter(meetingPhotoIsLinked);
    if (!photos.length) return;
    setUnlinkConfirmPhotos(photos);
  }, []);

  const confirmUnlinkSampleFromMedia = useCallback(async () => {
    const photos = unlinkConfirmPhotos || [];
    const photoIds = sanitizeMeetingImportPhotoIds(photos.map((p) => p.id));
    if (!photoIds.length) {
      setUnlinkConfirmPhotos(null);
      return;
    }
    setUnlinkingMuestra(true);
    setError("");
    try {
      const data = await unlinkMeetingPhotosMuestras(id, { photoIds });
      if (data && typeof data === "object" && Array.isArray(data.photos)) {
        setSession((prev) => {
          if (!prev) return data;
          return {
            ...prev,
            ...data,
            photos: data.photos,
            leftWithClient: Array.isArray(data.leftWithClient)
              ? data.leftWithClient
              : prev.leftWithClient || [],
          };
        });
      }
      mediaFeedRef.current?.clearSelection?.();
      setMediaSelectedCount(0);
      setMediaSelectionAllLinked(false);
      setUnlinkConfirmPhotos(null);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.unlinkMuestraError"),
      );
    } finally {
      setUnlinkingMuestra(false);
    }
  }, [id, t, unlinkConfirmPhotos]);

  const confirmLinkExistingMuestra = useCallback(
    async (muestra) => {
      const muestraId = Number(muestra?.ID_MUESTRA ?? muestra?.id);
      const photoIds = sanitizeMeetingImportPhotoIds(
        pendingSamplePhotos.map((p) => p.id),
      );
      if (!muestraId || !photoIds.length) return;
      setLinkingMuestra(true);
      setError("");
      try {
        const result = await importMeetingMediaToMuestra(muestraId, {
          sessionId: Number(id),
          photoIds,
        });
        applyImportedMeetingLinks(result);
        setLinkMuestraOpen(false);
        setPendingSamplePhotos([]);
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.message ||
            t("Meetings.linkMuestraError"),
        );
      } finally {
        setLinkingMuestra(false);
      }
    },
    [applyImportedMeetingLinks, id, pendingSamplePhotos, t],
  );

  const continueCreateSampleWithoutNotes = useCallback(() => {
    setEmptyTeamNoteWarnOpen(false);
    setProductGateOpen(true);
  }, []);

  const cancelCreateSampleEmptyNotes = useCallback(() => {
    setEmptyTeamNoteWarnOpen(false);
    setEmptyTeamNotePhotoIds([]);
    setPendingSamplePhotos([]);
  }, []);

  const closeNeedGroupWarn = useCallback(() => {
    setNeedGroupWarnOpen(false);
    setPendingSamplePhotos([]);
  }, []);

  const handleProductChosenForSample = useCallback(
    async (product) => {
      setError("");
      try {
        await ensureMuestraCatalogs();
        const notes = buildMeetingSampleNotes(pendingSamplePhotos, {
          labels: {
            photo: t("Meetings.photo"),
            video: t("Meetings.video"),
          },
        });
        const photoIds = sanitizeMeetingImportPhotoIds(
          pendingSamplePhotos.map((p) => p.id),
        );
        setSelectedProductForMuestra({
          id: product.id,
          ID_MARCA: product.ID_MARCA,
          nombre: product.nombre,
        });
        setNewMuestraDraft({
          MUESTRA: `SAMP-${Math.random()
            .toString(36)
            .substring(2, 7)
            .toUpperCase()}`,
          ID_ESTADO: 1,
          ID_Marca: product.ID_MARCA || "",
          ID_TIPO_MUESTRA: "",
          ID_TALLA_BASE: "",
          ID_COLOR_BASE: "",
          FECHA_PETICION: null,
          FECHA_PREVISTA: null,
          FECHA_RECEPCION: null,
          NOTAS: notes,
          PROVEEDOR: "",
          ID_RESPONSABLE_SUPPLIER: null,
          PRIORIDAD: null,
          FECHA_PREVISTA_FABRICA: null,
          CALIDAD_NOMBRE: "",
          CALIDAD_ESTADO: null,
          PRINT_NOMBRE: "",
          PRINT_ESTADO: null,
          ID_PRODUCTO: product.id,
        });
        setPendingMeetingMedia({
          sessionId: Number(id),
          photoIds,
        });
        setMuestraEditMode(false);
        setMuestraDrawerOpen(true);
      } catch (err) {
        pushMeetingError(err);
      }
    },
    [ensureMuestraCatalogs, id, pendingSamplePhotos, t],
  );

  const closeMuestraDrawer = useCallback(() => {
    setMuestraDrawerOpen(false);
    setNewMuestraDraft(null);
    setSelectedProductForMuestra(null);
    setPendingMeetingMedia(null);
    setMuestraEditMode(false);
  }, []);

  const handleDeleteMuestraFromMeeting = useCallback((muestraId) => {
    const idNum = Number(muestraId);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    setSession((prev) => {
      if (!prev?.photos?.length) return prev;
      let changed = false;
      const photosNext = prev.photos.map((p) => {
        const linked =
          Array.isArray(p.linkedMuestras) && p.linkedMuestras.length
            ? p.linkedMuestras
            : p.idMuestra
              ? [
                  {
                    idMuestra: p.idMuestra,
                    muestraNombre: p.muestraNombre || "",
                  },
                ]
              : [];
        if (!linked.some((x) => Number(x.idMuestra) === idNum)) return p;
        changed = true;
        const next = linked.filter((x) => Number(x.idMuestra) !== idNum);
        const last = next[next.length - 1];
        return {
          ...p,
          linkedMuestras: next,
          idMuestra: last?.idMuestra ?? null,
          muestraNombre: last?.muestraNombre || "",
        };
      });
      return changed ? { ...prev, photos: photosNext } : prev;
    });
  }, []);

  const handleLeaveSamplesDone = useCallback(
    async (updatedSession) => {
      if (updatedSession) {
        setSession((prev) => {
          if (!prev) return updatedSession;
          return {
            ...prev,
            ...updatedSession,
            photos: Array.isArray(updatedSession.photos)
              ? updatedSession.photos
              : prev.photos,
            audioChunks: Array.isArray(updatedSession.audioChunks)
              ? updatedSession.audioChunks
              : prev.audioChunks,
            leftWithClient: Array.isArray(updatedSession.leftWithClient)
              ? updatedSession.leftWithClient
              : prev.leftWithClient || [],
          };
        });
      }
      try {
        await load({ quiet: true, keepError: true });
      } catch {
        /* ignore */
      }
    },
    [load],
  );

  const sessionMoodboards = useMemo(() => {
    const list = Array.isArray(session?.moodboards) ? session.moodboards : [];
    return list.filter((m) => Number(m?.id) > 0);
  }, [session?.moodboards]);

  const sessionRecaps = useMemo(
    () =>
      sessionMoodboards.filter(
        (m) => String(m?.kind || "").toLowerCase() === "resumen",
      ),
    [sessionMoodboards],
  );

  const openSessionRecap = useCallback(
    (recapId) => {
      const idNum = Number(recapId);
      if (!idNum) return;
      setMoodboardMenuEl(null);
      navigate(`/skills/tareas?resumen=${idNum}`);
    },
    [navigate],
  );

  const openCreateRecapWithPhotos = useCallback((selected) => {
    setMoodboardPhotos(Array.isArray(selected) ? selected : []);
    setMoodboardOpen(true);
  }, []);

  const leftWithClient = useMemo(
    () =>
      Array.isArray(session?.leftWithClient) ? session.leftWithClient : [],
    [session?.leftWithClient],
  );

  useEffect(() => {
    if (leftWithClient.length > 0) setLeaveSamplesListOpen(true);
  }, [leftWithClient.length]);

  const requestUnleaveSample = useCallback(
    (item) => {
      const mid = Number(item?.idMuestra ?? item);
      if (!Number.isInteger(mid) || mid <= 0) return;
      const hit = leftWithClient.find((x) => Number(x.idMuestra) === mid) || {
        idMuestra: mid,
      };
      setUnleaveConfirmItem(hit);
    },
    [leftWithClient],
  );

  const confirmUnleaveSample = useCallback(async () => {
    const mid = Number(unleaveConfirmItem?.idMuestra);
    if (!Number.isInteger(mid) || mid <= 0) {
      setUnleaveConfirmItem(null);
      return;
    }
    setUnleavingSampleId(mid);
    setError("");
    try {
      const data = await unleaveMeetingSamples(id, { idMuestras: [mid] });
      setUnleaveConfirmItem(null);
      await handleLeaveSamplesDone(data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.leaveSamplesUndoError"),
      );
    } finally {
      setUnleavingSampleId(null);
    }
  }, [handleLeaveSamplesDone, id, t, unleaveConfirmItem]);

  const handleMuestraPersistedFromMeeting = useCallback(
    async (saved) => {
      if (!saved?.ID_MUESTRA) return;
      setMuestraEditMode(true);
      setNewMuestraDraft((prev) => ({
        ...(prev || {}),
        ...saved,
        ID_MUESTRA: saved.ID_MUESTRA,
      }));
      setPendingMeetingMedia(null);
      try {
        await load({ quiet: true, keepError: true });
      } catch {
        /* ignore */
      }
    },
    [load],
  );

  const openLinkedMuestra = useCallback(
    async (muestraId) => {
      if (!muestraId) return;
      setError("");
      try {
        await ensureMuestraCatalogs();
        const full = await getMuestraById(muestraId);
        setSelectedProductForMuestra({
          id: full.ID_PRODUCTO,
          ID_MARCA: full.ID_Marca,
          nombre: full.producto?.nombre || full.NombreCliente,
        });
        setNewMuestraDraft(full);
        setMuestraEditMode(true);
        setPendingMeetingMedia(null);
        setMuestraDrawerOpen(true);
      } catch (err) {
        pushMeetingError(err);
      }
    },
    [ensureMuestraCatalogs],
  );
  const audioByPhotoId = useMemo(
    () => audioCouplingByPhotoId(photos, audioChunks),
    [photos, audioChunks],
  );
  const previewPhoto = useMemo(
    () => photos.find((p) => Number(p.id) === Number(previewPhotoId)) || null,
    [photos, previewPhotoId],
  );
  /** Orden de galería: grupos (mediaIds) y luego sin agrupar. */
  const previewNavIds = useMemo(() => {
    const byId = new Map(
      (photos || [])
        .map((p) => [Number(p.id), p])
        .filter(([id]) => Number.isFinite(id) && id > 0),
    );
    const seen = new Set();
    const ids = [];
    for (const g of session?.mediaGroups || []) {
      for (const raw of g?.mediaIds || []) {
        const n = Number(raw);
        if (!byId.has(n) || seen.has(n)) continue;
        seen.add(n);
        ids.push(n);
      }
    }
    for (const p of photos || []) {
      const n = Number(p.id);
      if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
      seen.add(n);
      ids.push(n);
    }
    return ids;
  }, [photos, session?.mediaGroups]);
  const previewNavIndex = useMemo(() => {
    if (previewPhotoId == null) return -1;
    return previewNavIds.indexOf(Number(previewPhotoId));
  }, [previewNavIds, previewPhotoId]);
  const previewGroupMeta = useMemo(() => {
    const id = Number(previewPhotoId);
    if (!Number.isFinite(id) || id <= 0) return null;
    const groups = Array.isArray(session?.mediaGroups)
      ? session.mediaGroups
      : [];
    for (let i = 0; i < groups.length; i += 1) {
      const g = groups[i];
      const hit = (g?.mediaIds || []).some((raw) => Number(raw) === id);
      if (!hit) continue;
      return {
        id: String(g.id || ""),
        label: String(g.label || "").trim(),
        index: i,
        ungrouped: false,
      };
    }
    return { id: "", label: "", index: -1, ungrouped: true };
  }, [previewPhotoId, session?.mediaGroups]);
  const previewIsVideo =
    previewPhoto &&
    (previewPhoto.kind === "video" ||
      String(previewPhoto.mimeType || "")
        .toLowerCase()
        .startsWith("video/"));

  useEffect(() => {
    if (!previewOpen || !previewPhotoId || previewIsVideo) {
      return undefined;
    }
    let alive = true;
    const photo =
      photos.find((p) => Number(p.id) === Number(previewPhotoId)) || null;
    if (!photo) return undefined;
    const cached = cachedMeetingPhotoFile(photo);
    if (cached) setPreviewDragFile(cached);
    fetchMeetingPhotoAsFile(photo)
      .then((file) => {
        if (alive && file) setPreviewDragFile(file);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // photos cambia con el poll: no cancelar la carga del jpeg
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, previewPhotoId, previewIsVideo]);

  /** Clic en miniatura del feed: visor/reproductor de esa pieza (no la galería). */
  const openMedia = useCallback(
    (photoId) => {
      const idNum = Number(photoId);
      if (!Number.isFinite(idNum)) return;
      setPreviewPhotoId(idNum);
      setPreviewOpen(true);
      setHighlightPhotoId(idNum);
      void ensurePhotoAiDetail(idNum);
      const photo = photos.find((p) => Number(p.id) === idNum) || null;
      const isVideo =
        photo &&
        (photo.kind === "video" ||
          String(photo.mimeType || "")
            .toLowerCase()
            .startsWith("video/"));
      if (!photo || isVideo) {
        setPreviewDragFile(null);
        return;
      }
      const cached = cachedMeetingPhotoFile(photo);
      setPreviewDragFile(cached);
      fetchMeetingPhotoAsFile(photo)
        .then((file) => {
          if (file) setPreviewDragFile(file);
        })
        .catch(() => {});
    },
    [ensurePhotoAiDetail, photos],
  );

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewPhotoId(null);
  }, []);

  const goPreviewPrev = useCallback(() => {
    if (previewNavIndex <= 0) return;
    openMedia(previewNavIds[previewNavIndex - 1]);
  }, [openMedia, previewNavIds, previewNavIndex]);

  const goPreviewNext = useCallback(() => {
    if (previewNavIndex < 0 || previewNavIndex >= previewNavIds.length - 1) {
      return;
    }
    openMedia(previewNavIds[previewNavIndex + 1]);
  }, [openMedia, previewNavIds, previewNavIndex]);

  const handleLeaveSamplesPdf = useCallback(async () => {
    if (!leftWithClient.length || leaveSamplesPdfBusy) return;
    setLeaveSamplesPdfBusy(true);
    setError("");
    try {
      await downloadMeetingLeaveSamplesPdf({
        session,
        leftWithClient,
        photos: session?.photos || photos || [],
      });
    } catch (err) {
      setError(err.message || t("Meetings.leaveSamplesPdfError"));
    } finally {
      setLeaveSamplesPdfBusy(false);
    }
  }, [leftWithClient, leaveSamplesPdfBusy, session, photos, t]);

  const elapsedLabel = useMemo(
    () => formatElapsed(recorder.elapsedMs),
    [recorder.elapsedMs],
  );

  if (loading) {
    return (
      <Box sx={shellSx}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 320px) 1fr" },
            gap: 2,
            p: { xs: 1.25, md: 1.5 },
            minHeight: 0,
          }}
        >
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />
          </Stack>
          <Box>
            <Skeleton
              variant="rounded"
              height={48}
              sx={{ mb: 1.5, borderRadius: 2 }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                },
                gap: 1.5,
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={168}
                  sx={{ borderRadius: 2.5 }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!session) {
    return (
      <Box sx={shellSx}>
        <Alert severity="error">{error || t("Meetings.loadError")}</Alert>
        <Button sx={{ mt: 2 }} onClick={leaveToList}>
          {t("Meetings.back")}
        </Button>
      </Box>
    );
  }

  const timerClosed = Boolean(session.endedAt) || session.status === "ready";
  const timerLabel = recorder.isRecording
    ? elapsedLabel
    : timerClosed
      ? "—"
      : "00:00";
  const timerHint = recorder.isRecording
    ? t("Meetings.timerRecordingHint")
    : timerClosed
      ? t("Meetings.timerClosedHint")
      : t("Meetings.timerIdleHint");
  const wantPark = Boolean(embedded && isDesktopLayout && !captureRailOpen);
  const collapsedCaptureRail = !captureRailOpen ? (
    <CollapsedCaptureRail
      theme={theme}
      t={t}
      timerLabel={timerLabel}
      recording={recorder.isRecording}
      busy={busy}
      canRecord={canRecord}
      canTakePhoto={canTakePhoto}
      notesCount={(session.freeNotes || []).length || 0}
      isNarrow={isNarrow}
      parked={wantPark}
      onExpand={() => setCaptureRailOpenPersist(true)}
      onOpenNotes={() => setFreeNotesSheetOpen(true)}
      onOpenAi={() => openCollapsedRailSection("ai")}
      onOpenDetails={() => openCollapsedRailSection("details")}
      onStop={handleStopAudio}
      onStart={handleStart}
      onPhoto={openPhotoPicker}
      onVideo={openVideoPicker}
      onGallery={openGalleryPicker}
      onMediaFile={handleMediaFile}
      photoInputRef={photoInputRef}
      videoInputRef={videoInputRef}
      galleryInputRef={galleryInputRef}
    />
  ) : null;

  return (
    <Box sx={shellSx}>
      <Box sx={meetingSessionHeaderSx(theme)} className="meeting-no-print">
        <Box sx={{ px: { xs: 1.25, sm: 1.5 }, pt: 1, pb: 1 }}>
          <Stack
            direction={compactHeader ? "column" : "row"}
            alignItems={compactHeader ? "stretch" : "center"}
            spacing={compactHeader ? 0.5 : 1}
            sx={{ minWidth: 0 }}
          >
            {/* Móvil: fila 1. Escritorio: bloque izquierdo de la única fila. */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                minWidth: 0,
                flex: compactHeader ? "0 0 auto" : "1 1 auto",
                flexWrap: "nowrap",
              }}
            >
              <IconButton
                onClick={requestLeaveMeeting}
                size="small"
                aria-label={t("Meetings.back")}
                sx={{ flexShrink: 0 }}
              >
                <ArrowBackIosNewOutlinedIcon fontSize="small" />
              </IconButton>
              {!embedded ? (
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                  {titleEditing ? (
                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      value={titleDraft}
                      placeholder={t("Meetings.titlePlaceholder")}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onBlur={() => commitTitle(titleDraft)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitTitle(titleDraft);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          setTitleDraft(session.title || "");
                          setTitleEditing(false);
                        }
                      }}
                      inputProps={{
                        "aria-label": t("Meetings.sessionTitle"),
                        maxLength: 200,
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontWeight: 700,
                          fontSize: "1rem",
                          py: 0.5,
                        },
                      }}
                    />
                  ) : (
                    <Tooltip title={t("Meetings.editTitleHint")}>
                      <Typography
                        variant="subtitle1"
                        component="button"
                        type="button"
                        onClick={() => {
                          setTitleDraft(session.title || "");
                          setTitleEditing(true);
                        }}
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.25,
                          wordBreak: "break-word",
                          textAlign: "left",
                          border: 0,
                          p: 0,
                          m: 0,
                          bgcolor: "transparent",
                          color: "inherit",
                          cursor: "text",
                          font: "inherit",
                          width: "100%",
                          borderRadius: 0.5,
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                          ...(compactHeader
                            ? {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }
                            : null),
                        }}
                      >
                        {session.title || t("Meetings.untitled")}
                      </Typography>
                    </Tooltip>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      lineHeight: 1.35,
                      ...(compactHeader
                        ? {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }
                        : null),
                    }}
                  >
                    {[
                      session.meetingDate,
                      session.customerName || t("Meetings.noCustomer"),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                </Box>
              ) : null}
              <Box
                sx={{
                  ...meetingChipRowSx,
                  pt: 0,
                  pb: 0,
                  flex: "0 0 auto",
                  minWidth: 0,
                  flexWrap: "nowrap",
                  alignItems: "center",
                }}
              >
                {recorder.isRecording ? (
                  <Tooltip title={t("Meetings.audioRecordingOn")}>
                    <Chip
                      size="small"
                      color="success"
                      icon={
                        <MicNoneOutlinedIcon
                          sx={{ fontSize: "16px !important" }}
                        />
                      }
                      label={t("Meetings.audioRecordingOnShort")}
                      sx={{
                        fontWeight: 700,
                        "& .MuiChip-icon": { ml: 0.6, mr: -0.25 },
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  </Tooltip>
                ) : ["recording", "failed"].includes(session.status) ? (
                  <Tooltip title={t("Meetings.audioRecordingOff")}>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={
                        <MicOffOutlinedIcon
                          sx={{ fontSize: "16px !important" }}
                        />
                      }
                      label={t("Meetings.audioRecordingOffShort")}
                      sx={{
                        height: 26,
                        fontFamily:
                          'Manrope, "Helvetica Neue", Arial, sans-serif',
                        fontWeight: 600,
                        borderColor: "divider",
                        color: "text.secondary",
                        bgcolor: "transparent",
                        "& .MuiChip-icon": { ml: 0.6, mr: -0.25 },
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  </Tooltip>
                ) : null}
                {!session.mediaPurged ? (
                  <Tooltip
                    title={t("Meetings.mediaDaysLeftChipHint", {
                      days: mediaDaysLeftFromSession(session) ?? "—",
                    })}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("Meetings.mediaDaysLeftChip", {
                        days: mediaDaysLeftFromSession(session) ?? "—",
                      })}
                      sx={{
                        height: 26,
                        fontFamily:
                          'Manrope, "Helvetica Neue", Arial, sans-serif',
                        fontWeight: 600,
                        borderColor: "divider",
                        color: "text.secondary",
                        bgcolor: "transparent",
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("Meetings.mediaPurgedChip")}
                  />
                )}
                {leftWithClient.length ? (
                  <Tooltip
                    title={t("Meetings.leaveSamplesChipCount", {
                      count: leftWithClient.length,
                    })}
                  >
                    <Chip
                      size="small"
                      clickable
                      onClick={() => setLeaveSamplesListOpen((v) => !v)}
                      icon={
                        <ExpandMoreOutlinedIcon
                          sx={{
                            ml: "4px !important",
                            color: "#fff !important",
                            transform: leaveSamplesListOpen
                              ? "rotate(180deg)"
                              : "none",
                            transition: "transform 160ms ease",
                          }}
                        />
                      }
                      label={t("Meetings.leaveSamplesChipCountShort", {
                        count: leftWithClient.length,
                      })}
                      sx={{
                        fontWeight: 600,
                        fontFamily:
                          'Manrope, "Helvetica Neue", Arial, sans-serif',
                        bgcolor: "#7f9dac",
                        color: "#171717",
                        "&:hover": { bgcolor: "#5f7d8c" },
                      }}
                    />
                  </Tooltip>
                ) : null}
              </Box>
            </Stack>
            {/* Móvil: fila 2. Escritorio: resto de la misma fila. */}
            <Box
              sx={{
                ...meetingHeaderToolbarSx,
                justifyContent: compactHeader ? "flex-start" : "flex-end",
                flex: compactHeader ? "1 1 auto" : "0 1 auto",
                minWidth: 0,
                maxWidth: "100%",
                flexWrap: "nowrap",
                overflowX: "auto",
                overflowY: "visible",
                // padding vertical: el scrollport no corta Badges inset
                pt: 0.45,
                pb: 0.2,
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorX: "contain",
                scrollbarWidth: "thin",
                ml: compactHeader ? 0 : "auto",
                // iOS: hijos no se encogen ni se pisan dentro del scroll
                "& > *": { flex: "0 0 auto" },
              }}
            >
              <Box
                ref={setMediaToolbarHost}
                sx={{
                  // No usar display:contents en iOS: rompe overflow/scroll y solapa botones.
                  display: "inline-flex",
                  alignItems: "center",
                  gap: { xs: 0.4, sm: 0.55 },
                  flex: "0 0 auto",
                  flexShrink: 0,
                  minWidth: "max-content",
                  maxWidth: "none",
                  "&:empty": { display: "none" },
                  "& > *": { flex: "0 0 auto" },
                }}
              />
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: { xs: 0.4, sm: 0.65 },
                  flex: "0 0 auto",
                  flexWrap: "nowrap",
                  minWidth: "max-content",
                }}
              >
                {photos.length ? (
                  <Box
                    sx={{
                      ...meetingHeaderToolGroupSx(theme, { accent: true }),
                      flex: "0 0 auto",
                      // No overflowX aquí: en CSS fuerza overflow-y y corta el Badge
                    }}
                  >
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        ...meetingHeaderToolGroupLabelSx(true),
                        display: { xs: "none", lg: "inline" },
                      }}
                    >
                      {t("Meetings.toolGroupMedia")}
                    </Typography>
                    {mediaSelectedCount ? (
                      <Chip
                        size="small"
                        color="primary"
                        label={mediaSelectedCount}
                        sx={{
                          height: 20,
                          minWidth: 22,
                          fontWeight: 800,
                          fontSize: "0.68rem",
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                    ) : null}
                    <Tooltip title={t("Meetings.freeNotesTitle")}>
                      <IconButton
                        size="small"
                        onClick={() => setFreeNotesSheetOpen(true)}
                        aria-label={t("Meetings.freeNotesTitle")}
                        sx={{
                          ...meetingHeaderToolIconBtnSx,
                          color: "primary.main",
                        }}
                      >
                        <Badge
                          color="secondary"
                          badgeContent={(session.freeNotes || []).length || 0}
                          max={99}
                          invisible={!(session.freeNotes || []).length}
                          sx={meetingHeaderBadgeSx}
                        >
                          <NotesOutlinedIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Meetings.selectAllMedia")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            mediaFeedRef.current?.selectAllVisible?.()
                          }
                          disabled={!photos.length}
                          aria-label={t("Meetings.selectAllMedia")}
                          sx={meetingHeaderToolIconBtnSx}
                        >
                          <SelectAllOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Meetings.deselectAllMedia")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            mediaFeedRef.current?.clearSelection?.()
                          }
                          disabled={!mediaSelectedCount}
                          aria-label={t("Meetings.deselectAllMedia")}
                          sx={meetingHeaderToolIconBtnSx}
                        >
                          <DeselectOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        mediaSelectedCount
                          ? t("Meetings.mediaGroupAddFromSelection", {
                              count: mediaSelectedCount,
                            })
                          : t("Meetings.mediaGroupAdd")
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => mediaFeedRef.current?.addGroup?.()}
                          disabled={savingMediaGroups}
                          aria-label={t("Meetings.mediaGroupAdd")}
                          sx={meetingHeaderToolIconBtnSx}
                        >
                          <AddOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        mediaSelectionAllLinked
                          ? t("Meetings.unlinkMuestra")
                          : t("Meetings.linkMuestra")
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={
                            !mediaSelectedCount ||
                            linkingMuestra ||
                            unlinkingMuestra
                          }
                          onClick={() => {
                            if (mediaSelectionAllLinked) {
                              mediaFeedRef.current?.unlinkSample?.();
                            } else {
                              mediaFeedRef.current?.linkSample?.();
                            }
                          }}
                          aria-label={
                            mediaSelectionAllLinked
                              ? t("Meetings.unlinkMuestra")
                              : t("Meetings.linkMuestra")
                          }
                          sx={{
                            ...meetingHeaderToolIconBtnSx,
                            color: mediaSelectionAllLinked
                              ? "warning.main"
                              : undefined,
                          }}
                        >
                          {mediaSelectionAllLinked ? (
                            <LinkOffOutlinedIcon fontSize="small" />
                          ) : (
                            <LinkOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Meetings.createSample")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!mediaSelectedCount}
                          onClick={() => mediaFeedRef.current?.createSample?.()}
                          aria-label={t("Meetings.createSample")}
                          sx={{
                            ...meetingHeaderToolIconBtnSx,
                            color: mediaSelectedCount
                              ? "primary.main"
                              : undefined,
                          }}
                        >
                          <Inventory2OutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {allowMoodboard && sessionRecaps.length === 1 ? (
                      <Tooltip title={t("Meetings.goToRecap")}>
                        <Button
                          size="small"
                          variant="outlined"
                          disableElevation
                          onClick={() => openSessionRecap(sessionRecaps[0].id)}
                          aria-label={t("Meetings.goToRecap")}
                          startIcon={<OpenInNewOutlinedIcon fontSize="small" />}
                          sx={{
                            ...meetingHeaderBtnSx,
                            px: 1.15,
                            height: 28,
                            minHeight: 28,
                            maxHeight: 28,
                            whiteSpace: "nowrap",
                            color: "#171717",
                            bgcolor: "transparent",
                            border: "1px solid",
                            borderColor: "rgba(95,125,140,0.35)",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: "rgba(127,157,172,0.16)",
                              boxShadow: "none",
                            },
                          }}
                        >
                          {t("Meetings.goToRecap")}
                        </Button>
                      </Tooltip>
                    ) : null}
                    {allowMoodboard && sessionRecaps.length > 1 ? (
                      <Tooltip
                        title={t("Meetings.goToRecapCount", {
                          count: sessionRecaps.length,
                        })}
                      >
                        <Badge
                          badgeContent={sessionRecaps.length}
                          color="secondary"
                          overlap="rectangular"
                          sx={meetingHeaderBadgeSx}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            disableElevation
                            onClick={(e) => setMoodboardMenuEl(e.currentTarget)}
                            aria-label={t("Meetings.goToRecapCount", {
                              count: sessionRecaps.length,
                            })}
                            startIcon={
                              <OpenInNewOutlinedIcon fontSize="small" />
                            }
                            sx={{
                              ...meetingHeaderBtnSx,
                              px: 1.15,
                              height: 28,
                              minHeight: 28,
                              maxHeight: 28,
                              whiteSpace: "nowrap",
                              color: "#171717",
                              bgcolor: "transparent",
                              border: "1px solid",
                              borderColor: "rgba(95,125,140,0.35)",
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: "rgba(127,157,172,0.16)",
                                boxShadow: "none",
                              },
                            }}
                          >
                            {t("Meetings.goToRecap")}
                          </Button>
                        </Badge>
                      </Tooltip>
                    ) : null}
                    {allowMoodboard && sessionRecaps.length === 0 ? (
                      <Tooltip title={t("Meetings.createRecap")}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!mediaSelectedCount}
                            onClick={() => {
                              const openRecap = () => {
                                const ids = new Set(
                                  mediaFeedRef.current?.getSelectedPhotoIds?.() ||
                                    [],
                                );
                                const selected = photos.filter((p) =>
                                  ids.has(Number(p.id)),
                                );
                                openCreateRecapWithPhotos(selected);
                              };
                              const flush =
                                mediaFeedRef.current?.flushSaveGroups?.();
                              if (flush && typeof flush.then === "function") {
                                flush.then(openRecap).catch(openRecap);
                              } else {
                                openRecap();
                              }
                            }}
                            aria-label={t("Meetings.createRecap")}
                            startIcon={
                              <SummarizeOutlinedIcon fontSize="small" />
                            }
                            sx={{
                              ...meetingHeaderBtnSx,
                              px: 1.15,
                              height: 28,
                              minHeight: 28,
                              maxHeight: 28,
                              whiteSpace: "nowrap",
                              color: "#5f7d8c",
                              borderColor: "rgba(95,125,140,0.28)",
                              bgcolor: "transparent",
                              "&:hover": {
                                borderColor: "#7f9dac",
                                bgcolor: "rgba(127,157,172,0.12)",
                              },
                              "&.Mui-disabled": {
                                color: "text.disabled",
                                borderColor: "divider",
                              },
                            }}
                          >
                            {t("Meetings.createRecap")}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null}
                    <Tooltip title={t("Meetings.moveMedia")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!mediaSelectedCount || movingMedia}
                          onClick={() => setMoveMediaOpen(true)}
                          aria-label={t("Meetings.moveMedia")}
                          sx={meetingHeaderToolIconBtnSx}
                        >
                          <DriveFileMoveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {workbenchMediaCount > 0 ||
                    (session.freeNotes || []).length > 0 ||
                    (summary && summary.status !== "error") ? (
                      <Tooltip title={t("Meetings.printExport")}>
                        <IconButton
                          size="small"
                          onClick={printMeetingSummary}
                          aria-label={t("Meetings.printExport")}
                          sx={meetingHeaderToolIconBtnSx}
                        >
                          <PrintOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                ) : null}
                <Box
                  sx={{
                    ...meetingHeaderToolGroupSx(theme),
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      ...meetingHeaderToolGroupLabelSx(),
                      display: { xs: "none", lg: "inline" },
                    }}
                  >
                    {t("Meetings.toolGroupCliente")}
                  </Typography>
                  <Tooltip title={t("Meetings.leaveSamples")}>
                    <IconButton
                      size="small"
                      onClick={() => setLeaveSamplesOpen(true)}
                      aria-label={t("Meetings.leaveSamples")}
                      sx={meetingHeaderToolIconBtnSx}
                    >
                      <QrCodeScannerOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {leftWithClient.length ? (
                    <Tooltip title={t("Meetings.leaveSamplesPdf")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={leaveSamplesPdfBusy}
                          onClick={handleLeaveSamplesPdf}
                          aria-label={t("Meetings.leaveSamplesPdf")}
                          sx={{
                            ...meetingHeaderToolIconBtnSx,
                            color: "info.main",
                          }}
                        >
                          {leaveSamplesPdfBusy ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <Badge
                              color="info"
                              badgeContent={leftWithClient.length}
                              overlap="circular"
                              sx={meetingHeaderBadgeSx}
                            >
                              <PictureAsPdfOutlinedIcon fontSize="small" />
                            </Badge>
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
                {canDelete ? (
                  <Tooltip title={t("Meetings.delete")}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t("Meetings.delete")}
                        disabled={busy || recorder.isRecording || deleting}
                        onClick={() => setDeleteOpen(true)}
                        sx={meetingHeaderIconBtnSx}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
            </Box>
          </Stack>
          <Collapse in={leaveSamplesListOpen && leftWithClient.length > 0}>
            <Box
              sx={{
                mt: 0.75,
                px: 1,
                py: 0.85,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "rgba(106,27,154,0.28)",
                bgcolor: (th) =>
                  th.palette.mode === "dark"
                    ? "rgba(106,27,154,0.16)"
                    : "rgba(106,27,154,0.06)",
                maxHeight: { xs: 120, sm: 140 },
                overflowX: "auto",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.65,
                  fontWeight: 600,
                  fontFamily: 'Manrope, "Helvetica Neue", Arial, sans-serif',
                  color: "#5f7d8c",
                  letterSpacing: "-0.01em",
                }}
              >
                {t("Meetings.leaveSamplesListTitle")} · {leftWithClient.length}
              </Typography>
              <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                spacing={0.75}
                sx={{ minWidth: "min-content" }}
              >
                {leftWithClient.map((item) => (
                  <Chip
                    key={item.idMuestra}
                    size="small"
                    variant="filled"
                    label={t("Meetings.leaveSamplesChip", {
                      name:
                        item.muestraNombreCliente ||
                        item.muestraNombre ||
                        `#${item.idMuestra}`,
                    })}
                    onClick={() => {
                      const mid = Number(item.idMuestra);
                      if (!Number.isFinite(mid) || mid <= 0) return;
                      setSelectedProductForMuestra(null);
                      setNewMuestraDraft(null);
                      setPendingMeetingMedia(null);
                      setMuestraEditMode(true);
                      getMuestraById(mid)
                        .then((data) => {
                          setNewMuestraDraft(data);
                          setMuestraDrawerOpen(true);
                        })
                        .catch(() => {
                          setError(t("Meetings.linkMuestraError"));
                        });
                    }}
                    onDelete={() => {
                      if (unleavingSampleId) return;
                      requestUnleaveSample(item);
                    }}
                    title={t("Meetings.leaveSamplesUndoChip", {
                      name:
                        item.muestraNombreCliente ||
                        item.muestraNombre ||
                        `#${item.idMuestra}`,
                    })}
                    sx={{
                      fontWeight: 800,
                      bgcolor: "rgba(106,27,154,0.9)",
                      color: "#fff",
                      "& .MuiChip-deleteIcon": {
                        color: "rgba(255,255,255,0.85)",
                        "&:hover": { color: "#fff" },
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      </Box>

      <Box sx={meetingSessionBodySx}>
        {error ? (
          <Alert
            severity={
              error === t("Meetings.networkErrorBrief") ? "warning" : "error"
            }
            sx={{ mb: meetingSectionGap, flexShrink: 0 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        ) : null}

        {!error && session.summary?.errorMessage ? (
          <Alert severity="error" sx={{ mb: meetingSectionGap }}>
            {session.summary.errorMessage}
          </Alert>
        ) : null}

        {!error && session.status === "failed" ? (
          <Alert severity="warning" sx={{ mb: meetingSectionGap }}>
            {t("Meetings.summarizeError")}
          </Alert>
        ) : null}

        {mediaWarning ? (
          <Alert
            severity="warning"
            sx={{ mb: meetingSectionGap }}
            onClose={() => setMediaWarning("")}
          >
            {mediaWarning}
          </Alert>
        ) : null}

        {mediaUploadingCount > 0 ? (
          <Alert severity="info" sx={{ mb: meetingSectionGap }} icon={false}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} />
              <span>
                {t("Meetings.mediaUploadingBanner", {
                  count: mediaUploadingCount,
                })}
              </span>
            </Stack>
          </Alert>
        ) : null}

        {needsConsent ? (
          <Paper
            sx={{ ...dashGlassCardSx(theme), mb: meetingSectionGap }}
            elevation={0}
          >
            <MeetingConsentPanel
              value={consentDraft}
              onChange={setConsentDraft}
              disabled={savingConsent}
            />
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 1.5, minHeight: 52 }}
              disabled={
                savingConsent || !isMeetingConsentComplete(consentDraft)
              }
              onClick={handleSaveConsent}
            >
              {savingConsent ? (
                <CircularProgress size={18} />
              ) : (
                t("Meetings.consentSave")
              )}
            </Button>
          </Paper>
        ) : null}

        <Box
          sx={meetingDesktopSplitSx({
            railCollapsed: !captureRailOpen,
            // Solo ocultar la columna si el rail está aparcado en un slot
            // (si no hay slot —p. ej. Compás sin cablear— el rail colapsado
            // se queda inline y no desaparece).
            hideRail: Boolean(wantPark && captureSlotEl),
          })}
        >
          {wantPark && captureSlotEl ? (
            createPortal(collapsedCaptureRail, captureSlotEl)
          ) : (
            <Box
              sx={
                captureRailOpen
                  ? meetingDesktopAsideSx
                  : meetingDesktopAsideRailSx
              }
            >
              {collapsedCaptureRail}

              {captureRailOpen ? (
                <>
                  <Paper
                    sx={{
                      ...meetingCaptureCardSx(theme),
                      position: "relative",
                    }}
                    elevation={0}
                  >
                    <Tooltip title={t("Meetings.captureRailCollapse")}>
                      <IconButton
                        onClick={() => setCaptureRailOpenPersist(false)}
                        aria-label={t("Meetings.captureRailCollapse")}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          width: 36,
                          height: 36,
                          borderRadius: 999,
                          color: "#5f7d8c",
                          border: "1px solid",
                          borderColor: "rgba(127,157,172,0.45)",
                          bgcolor: "rgba(127,157,172,0.12)",
                          boxShadow: "none",
                          "&:hover": {
                            bgcolor: "rgba(127,157,172,0.22)",
                            borderColor: "#7f9dac",
                          },
                        }}
                      >
                        <ChevronLeftOutlinedIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontFamily:
                          'Manrope, "Helvetica Neue", Arial, sans-serif',
                        letterSpacing: "-0.02em",
                        fontVariantNumeric: "tabular-nums",
                        mb: 0.25,
                        fontSize: {
                          xs: "2.25rem",
                          sm: "2.75rem",
                          md: "2.5rem",
                        },
                        textAlign: "center",
                      }}
                    >
                      {timerLabel}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1, textAlign: "center", px: 0.5 }}
                    >
                      {timerHint}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5, textAlign: "center" }}
                    >
                      {t("Meetings.counts", {
                        audio: session.audioCount || 0,
                        photos: session.imageCount ?? session.photoCount ?? 0,
                        videos: session.videoCount || 0,
                      })}
                      {recorder.pendingUploads
                        ? ` · ${t("Meetings.uploading", {
                            count: recorder.pendingUploads,
                          })}`
                        : ""}
                    </Typography>

                    {recorder.isRecording ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 1.25, textAlign: "center", px: 0.5 }}
                      >
                        {t("Meetings.keepAwake")}
                      </Typography>
                    ) : null}

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: { xs: 0.65, sm: 0.85 },
                        width: "100%",
                      }}
                    >
                      {recorder.isRecording ? (
                        <Button
                          disableElevation
                          onClick={handleStopAudio}
                          disabled={busy}
                          aria-label={t("Meetings.stop")}
                          sx={meetingCaptureActionTileSx("#c62828")}
                        >
                          <StopCircleOutlinedIcon
                            sx={{ fontSize: { xs: 26, sm: 28 } }}
                          />
                          <Typography
                            component="span"
                            sx={{
                              fontSize: { xs: "0.68rem", sm: "0.75rem" },
                              fontWeight: 600,
                              letterSpacing: "-0.01em",
                              fontFamily:
                                'Manrope, "Helvetica Neue", Arial, sans-serif',
                            }}
                          >
                            {t("Meetings.stop")}
                          </Typography>
                        </Button>
                      ) : (
                        <Button
                          disableElevation
                          onClick={handleStart}
                          disabled={!canRecord || busy}
                          aria-label={t("Meetings.record")}
                          sx={meetingCaptureActionTileSx("#5f7d8c")}
                        >
                          <MicNoneOutlinedIcon
                            sx={{ fontSize: { xs: 26, sm: 28 } }}
                          />
                          <Typography
                            component="span"
                            sx={{
                              fontSize: { xs: "0.68rem", sm: "0.75rem" },
                              fontWeight: 600,
                              letterSpacing: "-0.01em",
                              fontFamily:
                                'Manrope, "Helvetica Neue", Arial, sans-serif',
                            }}
                          >
                            {t("Meetings.record")}
                          </Typography>
                        </Button>
                      )}
                      <Button
                        disableElevation
                        onClick={openPhotoPicker}
                        disabled={!canTakePhoto}
                        aria-label={t("Meetings.photo")}
                        sx={meetingCaptureActionTileSx("#7f9dac")}
                      >
                        <PhotoCameraOutlinedIcon
                          sx={{ fontSize: { xs: 26, sm: 28 } }}
                        />
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: "0.68rem", sm: "0.75rem" },
                            fontWeight: 600,
                            letterSpacing: "-0.01em",
                            fontFamily:
                              'Manrope, "Helvetica Neue", Arial, sans-serif',
                          }}
                        >
                          {t("Meetings.photo")}
                        </Typography>
                      </Button>
                      <Button
                        disableElevation
                        onClick={openVideoPicker}
                        disabled={!canTakePhoto}
                        aria-label={t("Meetings.video")}
                        sx={meetingCaptureActionTileSx("#7f9dac")}
                      >
                        <VideocamOutlinedIcon
                          sx={{ fontSize: { xs: 26, sm: 28 } }}
                        />
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: "0.68rem", sm: "0.75rem" },
                            fontWeight: 600,
                            letterSpacing: "-0.01em",
                            fontFamily:
                              'Manrope, "Helvetica Neue", Arial, sans-serif',
                          }}
                        >
                          {t("Meetings.video")}
                        </Typography>
                      </Button>
                      <Button
                        disableElevation
                        onClick={openGalleryPicker}
                        disabled={!canTakePhoto}
                        aria-label={t("Meetings.fromGallery")}
                        sx={meetingCaptureActionTileSx("#7f9dac")}
                      >
                        <CollectionsOutlinedIcon
                          sx={{ fontSize: { xs: 26, sm: 28 } }}
                        />
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: "0.68rem", sm: "0.75rem" },
                            fontWeight: 600,
                            letterSpacing: "-0.01em",
                            fontFamily:
                              'Manrope, "Helvetica Neue", Arial, sans-serif',
                            textAlign: "center",
                            px: 0.15,
                          }}
                        >
                          {t("Meetings.fromGallery")}
                        </Typography>
                      </Button>
                    </Box>
                    {canMarkReady ? (
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="large"
                        startIcon={<FlagOutlinedIcon />}
                        onClick={handleMarkReady}
                        disabled={busy}
                        sx={{
                          ...meetingActionBtnSx,
                          mt: 1,
                          color: "text.secondary",
                          borderColor: "divider",
                        }}
                      >
                        {t("Meetings.endMeeting")}
                      </Button>
                    ) : null}
                    {recorder.isPausedForMedia ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1, textAlign: "center" }}
                      >
                        {t("Meetings.videoPausesAudio")}
                      </Typography>
                    ) : null}
                    {recordingHeldByOther ? (
                      <Alert severity="info" sx={{ mt: 1.25, py: 0.5 }}>
                        {t("Meetings.recordingByOther", {
                          name:
                            session.recordingByUsername ||
                            t("Meetings.freeNotesAnonymous"),
                        })}
                      </Alert>
                    ) : null}
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*,image/heic,image/heif,.heic,.heif"
                      capture={isNarrow ? "environment" : undefined}
                      hidden
                      onChange={handleMediaFile}
                    />
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      multiple
                      capture={isNarrow ? "environment" : undefined}
                      hidden
                      onChange={handleMediaFile}
                    />
                    <input
                      ref={galleryInputRef}
                      type="file"
                      accept="image/*,image/heic,image/heif,.heic,.heif,video/*,application/pdf,.pdf"
                      multiple
                      hidden
                      onChange={handleMediaFile}
                    />
                  </Paper>

                  <Box id="meeting-free-notes">
                    <MeetingFreeNotesPanel
                      notes={session.freeNotes || []}
                      currentUserId={currentUserId}
                      canModerate={canDelete}
                      saving={noteSaving}
                      onCreate={handleCreateFreeNote}
                      onUpdate={handleUpdateFreeNote}
                      onDelete={handleDeleteFreeNote}
                      dense
                    />
                  </Box>

                  <Paper
                    sx={{ ...meetingStackedPanelSx(theme), p: 0 }}
                    elevation={0}
                    data-meeting-ai
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.15,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 999,
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "rgba(127,157,172,0.14)",
                          color: "#5f7d8c",
                        }}
                      >
                        <RefreshOutlinedIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.25,
                            fontFamily:
                              'Manrope, "Helvetica Neue", Arial, sans-serif',
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {t("Meetings.aiActionsTitle")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.35, lineHeight: 1.4 }}
                        >
                          {t("Meetings.regenMediaShortHint")}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          startIcon={
                            regenMediaBusy ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <RefreshOutlinedIcon />
                            )
                          }
                          disabled={!canSummarizeNow || busy || regenMediaBusy}
                          onClick={() => setRegenMediaOpen(true)}
                          sx={{
                            ...meetingStudioContainedBtnSx,
                            mt: 1.1,
                            px: 1.75,
                            minHeight: 40,
                          }}
                        >
                          {t("Meetings.regenMedia")}
                        </Button>
                        {!canSummarizePermission ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.85, lineHeight: 1.4 }}
                          >
                            {t("Meetings.summarizeOwnerOnlyHint")}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Paper>

                  <Paper
                    sx={{
                      ...meetingStackedPanelSx(theme),
                      p: 0,
                      overflow: "visible",
                    }}
                    elevation={0}
                    data-meeting-details
                  >
                    <Accordion
                      disableGutters
                      elevation={0}
                      defaultExpanded={!summaryDone}
                      onChange={(_e, expanded) => {
                        if (!expanded) return;
                        requestAnimationFrame(() => {
                          document
                            .querySelector("[data-meeting-details]")
                            ?.scrollIntoView?.({
                              block: "nearest",
                              behavior: "smooth",
                            });
                        });
                      }}
                      sx={{
                        bgcolor: "transparent",
                        "&:before": { display: "none" },
                        boxShadow: "none",
                        overflow: "visible",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreOutlinedIcon />}
                        sx={{
                          px: 1.75,
                          minHeight: 52,
                          "& .MuiAccordionSummary-content": {
                            my: 1.1,
                            alignItems: "center",
                            gap: 1.25,
                          },
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              lineHeight: 1.2,
                              fontFamily:
                                'Manrope, "Helvetica Neue", Arial, sans-serif',
                              letterSpacing: "-0.02em",
                            }}
                          >
                            {t("Meetings.sessionDetails")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mt: 0.35,
                              lineHeight: 1.35,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontFamily:
                                'Manrope, "Helvetica Neue", Arial, sans-serif',
                            }}
                          >
                            {[
                              customerDraft || null,
                              dateDraft || null,
                              attendeeIds.length
                                ? t("Meetings.detailsAttendeesShort", {
                                    count: attendeeIds.length,
                                  })
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 1.75, pb: 2, pt: 0.25 }}>
                        <Stack spacing={1.35} sx={{ minWidth: 0 }}>
                          <TextField
                            label={t("Meetings.sessionTitle")}
                            fullWidth
                            size="small"
                            value={titleDraft}
                            placeholder={t("Meetings.titlePlaceholder")}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onBlur={() => commitTitle(titleDraft)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                              }
                            }}
                            inputProps={{ maxLength: 200 }}
                            sx={meetingQuietFieldSx}
                          />
                          <MeetingBrandSelect
                            value={customerDraft}
                            onChange={(name) => {
                              setCustomerDraft(name);
                              setClientContactIds([]);
                              if (name !== (session.customerName || "")) {
                                patchMeetingSession(id, {
                                  customerName: name,
                                  clientContactIds: [],
                                })
                                  .then(setSession)
                                  .catch((err) => pushMeetingError(err));
                              }
                            }}
                          />
                          <TextField
                            type="date"
                            label={t("Meetings.date")}
                            fullWidth
                            size="small"
                            value={dateDraft}
                            onChange={(e) => {
                              const next = e.target.value;
                              setDateDraft(next);
                              if (
                                next &&
                                next !== (session.meetingDate || "")
                              ) {
                                patchMeetingSession(id, { meetingDate: next })
                                  .then(setSession)
                                  .catch((err) => pushMeetingError(err));
                              }
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={meetingQuietFieldSx}
                          />
                          <MeetingAttendeeSelect
                            value={attendeeIds}
                            onChange={(ids) => {
                              setAttendeeIds(ids);
                              patchMeetingSession(id, {
                                attendeeUserIds: ids,
                              })
                                .then(setSession)
                                .catch((err) => pushMeetingError(err));
                            }}
                          />
                          <MeetingClientContactSelect
                            brandName={customerDraft}
                            value={clientContactIds}
                            onChange={(ids) => {
                              setClientContactIds(ids);
                              patchMeetingSession(id, {
                                clientContactIds: ids,
                              })
                                .then(setSession)
                                .catch((err) => pushMeetingError(err));
                            }}
                          />
                          {!needsConsent ? (
                            <Typography
                              variant="caption"
                              sx={{
                                lineHeight: 1.45,
                                color: "#3f7354",
                                fontFamily:
                                  'Manrope, "Helvetica Neue", Arial, sans-serif',
                              }}
                            >
                              {t("Meetings.consentRecorded", {
                                user: session.consentByUsername || "—",
                                date: session.consentAt
                                  ? new Date(session.consentAt).toLocaleString()
                                  : "—",
                              })}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.45,
                              fontFamily:
                                'Manrope, "Helvetica Neue", Arial, sans-serif',
                            }}
                          >
                            {session.mediaPurged
                              ? t("Meetings.mediaPurgedBanner")
                              : t("Meetings.retentionSessionBanner", {
                                  days:
                                    mediaDaysLeftFromSession(session) ?? "—",
                                  total: MEETING_MEDIA_RETENTION_DAYS,
                                  warnMb: formatMeetingMediaWarnMb(),
                                  photoMb: formatMeetingPhotoMaxMb(),
                                  videoMb: formatMeetingVideoMaxMb(),
                                })}
                          </Typography>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Paper>
                </>
              ) : null}
            </Box>
          )}

          <Box
            sx={[
              meetingDesktopMainSx,
              wantPark && captureSlotEl ? { gridColumn: { md: "1" } } : null,
            ]}
          >
            <Paper
              sx={{
                ...meetingStackedPanelSx(theme),
                flexShrink: 0,
                maxHeight: { md: "42%" },
                overflowX: "hidden",
                overflowY: { md: "auto" },
              }}
              elevation={0}
            >
              <Accordion
                disableGutters
                elevation={0}
                defaultExpanded={!workbenchMode}
                sx={{
                  bgcolor: "transparent",
                  "&:before": { display: "none" },
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreOutlinedIcon />}
                  sx={{ px: 1.5, minHeight: 48 }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    {t("Meetings.summary")}
                    {summary && summary.status !== "error"
                      ? ` · ${t("Meetings.audioChunksTitle")} · ${
                          audioChunks.length || session.audioCount || 0
                        }`
                      : ""}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pb: 2, pt: 0 }}>
                  <Stack spacing={2}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="meeting-no-print"
                    >
                      {t("Meetings.summaryPrintHint")}
                    </Typography>
                    <Box className="meeting-no-print">
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        {t("Meetings.audioChunksTitle")}
                        {` · ${audioChunks.length || session.audioCount || 0}`}
                      </Typography>
                      {audioChunks.length ? (
                        <Stack spacing={1}>
                          {audioChunks.map((chunk, idx) => (
                            <Box
                              key={chunk.id || idx}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "120px minmax(0, 1fr)",
                                },
                                gap: 1,
                                alignItems: "center",
                                p: 1,
                                borderRadius: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {t("Meetings.audioChunkLabel", {
                                  n: (chunk.seq ?? idx) + 1,
                                })}
                                {chunk.hasTranscript ? " · ✓" : ""}
                              </Typography>
                              {chunk.fileUrl ? (
                                <Box
                                  component="audio"
                                  controls
                                  preload="metadata"
                                  src={meetingMediaUrl(chunk.fileUrl)}
                                  sx={{
                                    width: "100%",
                                    maxWidth: 420,
                                    height: 36,
                                  }}
                                />
                              ) : null}
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t("Meetings.audioChunkEmpty")}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        {t("Meetings.audioInSummaryHint")}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {photos.length ? (
              <Paper
                elevation={0}
                className="meeting-no-print"
                sx={{
                  ...meetingStackedPanelSx(theme),
                  flex: { md: 1 },
                  minHeight: { md: 0 },
                  display: { md: "flex" },
                  flexDirection: { md: "column" },
                  overflow: { md: "hidden" },
                }}
              >
                <MeetingMediaGroupsFeed
                  ref={mediaFeedRef}
                  photos={photos}
                  mediaGroups={session?.mediaGroups || EMPTY_MEDIA_GROUPS}
                  notesById={notesById}
                  audioByPhotoId={audioByPhotoId}
                  summaryLang={summaryLang}
                  highlightPhotoId={highlightPhotoId}
                  expandedNoteIds={expandedNoteIds}
                  setExpandedNoteIds={setExpandedNoteIds}
                  openMedia={openMedia}
                  summary={displaySummary}
                  onSaveGroups={handleSaveMediaGroups}
                  savingGroups={savingMediaGroups}
                  onSaveTeamNote={handleSaveTeamNote}
                  onDeleteMedia={handleDeleteMedia}
                  onCreateSample={handleCreateSampleFromMedia}
                  onLinkSample={handleLinkSampleFromMedia}
                  onUnlinkSample={handleUnlinkSampleFromMedia}
                  onOpenLinkedSample={openLinkedMuestra}
                  onSelectionChange={handleMediaSelectionChange}
                  initialLayoutMode={mediaLayoutMode}
                  toolbarHost={mediaToolbarHost}
                  onDropFiles={handleDropMediaFiles}
                />
              </Paper>
            ) : (
              <Paper sx={dashGlassCardSx(theme)} elevation={0}>
                <Typography color="text.secondary">
                  {t("Meetings.mediaGallery")} — 0
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      <MeetingTeamNoteCaptureDialog
        open={captionOpen}
        onClose={() => setCaptionOpen(false)}
        onSave={saveCaption}
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        isNarrow={isNarrow}
        isVideo={captionIsVideo}
        uploading={String(captionPhotoId || "").startsWith("local-")}
      />

      <MeetingMediaPreviewDialog
        open={previewOpen}
        photo={previewPhoto}
        onClose={closePreview}
        isNarrow={isNarrow}
        notesById={notesById}
        audioByPhotoId={audioByPhotoId}
        summaryLang={summaryLang}
        previewDragFile={previewDragFile}
        onSaveTeamNote={handleSaveTeamNote}
        onRotatePhoto={handleRotatePhoto}
        onOpenLinkedSample={openLinkedMuestra}
        onPrev={goPreviewPrev}
        onNext={goPreviewNext}
        canPrev={previewNavIndex > 0}
        canNext={
          previewNavIndex >= 0 && previewNavIndex < previewNavIds.length - 1
        }
        navIndex={previewNavIndex}
        navTotal={previewNavIds.length}
        groupMeta={previewGroupMeta}
      />

      {/* Documento de impresión: portal a body (fuera del acordeón) */}
      <MeetingSummaryPrint
        session={session}
        summary={summary && summary.status !== "error" ? displaySummary : null}
        summaryLang={summaryLang}
        onSummaryLangChange={setSummaryLang}
        hasSummaryEn={Boolean(summary?.en)}
        onOpenMedia={openMedia}
        hideActions
        compact
        printOnly
      />

      <MeetingRegenMediaDialog
        open={regenMediaOpen}
        photos={photos}
        busy={regenMediaBusy}
        onClose={() => setRegenMediaOpen(false)}
        onConfirm={handleRegenMedia}
      />

      <MeetingMoveMediaDialog
        open={moveMediaOpen}
        onClose={() => !movingMedia && setMoveMediaOpen(false)}
        currentSessionId={id}
        selectedCount={mediaSelectedCount}
        moving={movingMedia}
        onConfirm={handleConfirmMoveMedia}
      />

      <MeetingDeleteDialog
        open={deleteOpen}
        session={session}
        deleting={deleting}
        onClose={() => !deleting && setDeleteOpen(false)}
        onConfirm={handleDelete}
      />

      <Dialog
        open={leaveUploadOpen}
        onClose={() => !leavingWait && setLeaveUploadOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isNarrow}
      >
        <DialogTitle>{t("Meetings.mediaUploadingLeaveTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.mediaUploadingLeaveBody", {
              count: mediaUploadingCount || 1,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button onClick={leaveAnyway} disabled={leavingWait} color="inherit">
            {t("Meetings.mediaLeaveAnyway")}
          </Button>
          <Button
            onClick={leaveAfterUploads}
            variant="contained"
            disabled={leavingWait}
            startIcon={
              leavingWait ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {leavingWait
              ? t("Meetings.mediaUploadingWait")
              : t("Meetings.mediaWaitUpload")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={needGroupWarnOpen}
        onClose={closeNeedGroupWarn}
        fullWidth
        maxWidth="xs"
        fullScreen={isNarrow}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <DialogTitle>{t("Meetings.createSampleNeedGroupTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.createSampleNeedGroupBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            onClick={closeNeedGroupWarn}
            sx={{ minHeight: 48 }}
          >
            {t("Meetings.createSampleNeedGroupOk")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emptyTeamNoteWarnOpen}
        onClose={cancelCreateSampleEmptyNotes}
        fullWidth
        maxWidth="xs"
        fullScreen={isNarrow}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <DialogTitle>{t("Meetings.createSampleEmptyNotesTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {t("Meetings.createSampleEmptyNotesBody", {
              count: emptyTeamNotePhotoIds.length,
              total: pendingSamplePhotos.length,
            })}
          </DialogContentText>
          {emptyTeamNotePhotoIds.length ? (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              {t("Meetings.createSampleEmptyNotesIds", {
                ids: emptyTeamNotePhotoIds.map((id) => `#${id}`).join(", "),
              })}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            pb: 2,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
            "& > :not(style)": { m: 0 },
          }}
        >
          <Button fullWidth={isNarrow} onClick={cancelCreateSampleEmptyNotes}>
            {t("Meetings.createSampleEmptyNotesBack")}
          </Button>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            color="warning"
            onClick={continueCreateSampleWithoutNotes}
            sx={{ minHeight: 48 }}
          >
            {t("Meetings.createSampleEmptyNotesContinue")}
          </Button>
        </DialogActions>
      </Dialog>

      <MeetingCreateSampleDialog
        open={productGateOpen}
        onClose={() => setProductGateOpen(false)}
        onProductChosen={handleProductChosenForSample}
        mediaCount={pendingSamplePhotos.length}
      />

      <Menu
        anchorEl={moodboardMenuEl}
        open={Boolean(moodboardMenuEl)}
        onClose={() => setMoodboardMenuEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {sessionRecaps.map((mb) => (
          <MenuItem
            key={mb.id}
            onClick={() => openSessionRecap(mb.id)}
            sx={{ maxWidth: 360 }}
          >
            <Typography noWrap variant="body2" sx={{ fontWeight: 600 }}>
              {mb.titulo || `#${mb.id}`}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      <MeetingCreateMoodboardDialog
        open={moodboardOpen}
        onClose={() => {
          setMoodboardOpen(false);
          setMoodboardPhotos([]);
        }}
        session={session}
        selectedPhotos={moodboardPhotos}
        defaultBranch="resumen"
        onCreated={(created) => {
          if (!created?.id) return;
          if (created.kind === "resumen") {
            navigate(`/skills/tareas?resumen=${created.id}`);
            return;
          }
          navigate(`/skills/tareas?moodboard=${created.id}`);
        }}
      />

      <MeetingLinkMuestraDialog
        open={linkMuestraOpen}
        onClose={() => {
          if (linkingMuestra) return;
          setLinkMuestraOpen(false);
          setPendingSamplePhotos([]);
        }}
        onConfirmMuestra={confirmLinkExistingMuestra}
        onConfirmProduct={async (product) => {
          setLinkMuestraOpen(false);
          await handleProductChosenForSample(product);
        }}
        mediaCount={pendingSamplePhotos.length}
        linking={linkingMuestra}
        fullScreen={isNarrow}
      />

      <Dialog
        open={freeNotesSheetOpen}
        onClose={() => setFreeNotesSheetOpen(false)}
        fullScreen={isNarrow}
        fullWidth
        maxWidth="sm"
        transitionDuration={160}
        sx={{ zIndex: (th) => th.zIndex.modal + 2 }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isNarrow ? 0 : 3,
            overflow: "hidden",
            maxHeight: isNarrow ? "100%" : "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t("Meetings.freeNotesTitle")}
          </Typography>
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={() => setFreeNotesSheetOpen(false)}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <MeetingFreeNotesPanel
            notes={session.freeNotes || []}
            currentUserId={currentUserId}
            canModerate={canDelete}
            saving={noteSaving}
            onCreate={handleCreateFreeNote}
            onUpdate={handleUpdateFreeNote}
            onDelete={handleDeleteFreeNote}
            dense
            hideChrome
          />
        </DialogContent>
      </Dialog>

      <MeetingLeaveSamplesDialog
        open={leaveSamplesOpen}
        onClose={() => setLeaveSamplesOpen(false)}
        session={session}
        onDone={handleLeaveSamplesDone}
        allowRecap={allowMoodboard}
        onCreateRecap={({ photoIds, session: updated }) => {
          const idSet = new Set(
            (Array.isArray(photoIds) ? photoIds : [])
              .map(Number)
              .filter((n) => Number.isInteger(n) && n > 0),
          );
          const pool = [
            ...(Array.isArray(updated?.photos) ? updated.photos : []),
            ...(Array.isArray(photos) ? photos : []),
          ];
          const seen = new Set();
          const selected = [];
          for (const p of pool) {
            const pid = Number(p?.id);
            if (!idSet.has(pid) || seen.has(pid)) continue;
            seen.add(pid);
            selected.push(p);
          }
          openCreateRecapWithPhotos(selected);
        }}
      />

      <MeetingUnlinkMuestraDialog
        open={Boolean(unlinkConfirmPhotos?.length)}
        onClose={() => {
          if (!unlinkingMuestra) setUnlinkConfirmPhotos(null);
        }}
        onConfirm={confirmUnlinkSampleFromMedia}
        busy={unlinkingMuestra}
        count={unlinkConfirmPhotos?.length || 1}
      />

      <MeetingUnleaveSamplesDialog
        open={Boolean(unleaveConfirmItem)}
        onClose={() => {
          if (!unleavingSampleId) setUnleaveConfirmItem(null);
        }}
        onConfirm={confirmUnleaveSample}
        busy={Boolean(unleavingSampleId)}
        count={1}
        sampleName={
          unleaveConfirmItem?.muestraNombreCliente ||
          unleaveConfirmItem?.muestraNombre ||
          ""
        }
      />

      {newMuestraDraft && muestraCatalogs ? (
        <DrawerMuestraV2
          open={muestraDrawerOpen}
          onClose={closeMuestraDrawer}
          isEditMode={muestraEditMode}
          newMuestra={newMuestraDraft}
          selectedProduct={selectedProductForMuestra}
          tiposMuestra={muestraCatalogs.tiposMuestra}
          tiposEstado={muestraCatalogs.tiposEstado}
          tiposTallas={muestraCatalogs.tiposTallas}
          tiposColores={muestraCatalogs.tiposColores}
          tiposProveedores={muestraCatalogs.tiposProveedores}
          tiposMarcas={muestraCatalogs.tiposMarcas}
          escandalloGrupos={muestraCatalogs.escandalloGrupos}
          escandallosIniciales={[]}
          userCanCreateSample={userCanCreateSample}
          pendingMeetingMedia={pendingMeetingMedia}
          orderEmailAttachMode="fichaPdf"
          keepOpenAfterSave
          onDeleteMuestra={handleDeleteMuestraFromMeeting}
          onMeetingMediaImported={(result) => {
            setPendingMeetingMedia(null);
            applyImportedMeetingLinks(result);
          }}
          onMuestraPersisted={handleMuestraPersistedFromMeeting}
        />
      ) : null}
    </Box>
  );
}
